import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Play, Pause, Trash2, Pencil, Users, Mail, Clock, ChevronRight, MoreVertical, Search, Crown, User, AlertTriangle, MoveRight, RotateCcw, WifiOff, Copy, Loader2, ShieldCheck, AlertCircle, Snowflake, Flame, Info } from "lucide-react";
import { Link } from "wouter";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { cn, getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AIDraftButton } from "@/components/ai-draft-button";
import { WarmVolumeAckDialog, type MassEmailSettingsLite } from "@/components/email/cold-warm-controls";
import { DripStepVariantsEditor } from "@/components/drip-step-variants-editor";
import { DripAnalyticsPanel, DripLeaderboardCard } from "@/components/drip-analytics-panel";
import { describeSendError } from "@/lib/send-guard";

type DripCampaign = {
  id: string;
  name: string;
  status: "draft" | "active" | "paused";
  scope: "MASTER" | "USER";
  ownerUserId?: string | null;
  assignedToUserId?: string | null;
  createdByUserId: string;
  fromName?: string | null;
  fromEmail?: string | null;
  autoEnrollNewLeads?: boolean;
  autoEnrollDelayMinutes?: number;
  createdAt: string;
  stepCount?: number;
  enrollmentCount?: number;
  activeEnrollments?: number;
  canEdit?: boolean;
  senderHasGmail?: boolean;
  senderEmail?: string;
  fallbackEnabled?: boolean;
  triggerType?: string | null;
  targetSegmentEntity?: string | null;
  riskTier?: "warm" | "cold" | null;
  maxSendsPerDay?: number | null;
  forwardColdReplies?: boolean;
  sentToday?: number;
};

type DripCampaignStep = {
  id: string;
  campaignId: string;
  stepOrder: number;
  delayMinutes: number;
  subjectTemplate: string;
  bodyTemplate: string;
  pauseOnReply: boolean;
  sendAsReply?: boolean;
  sendAtTime?: string | null;
  activeCount?: number;
  pausedCount?: number;
};

type DripEnrollment = {
  id: string;
  campaignId: string;
  contactId: string;
  contactType: string;
  contactEmail: string;
  contactName?: string | null;
  currentStepIndex: number;
  nextSendAt?: string | null;
  status: "active" | "completed" | "paused" | "suppressed";
  createdAt: string;
};

type DripPermissions = {
  isAdmin: boolean;
  userId: string;
  userEmail?: string;
  canViewCampaigns: boolean;
  canCreateCampaigns: boolean;
  canEditCampaigns: boolean;
  canDeleteCampaigns: boolean;
  canEnrollContacts: boolean;
  canPauseCampaigns: boolean;
};

type DripUser = {
  id: string;
  name: string;
  email: string;
  role: string;
};

type SortableStepCardProps = {
  step: DripCampaignStep;
  idx: number;
  canEdit: boolean;
  formatDelay: (m: number) => string;
  onEdit: () => void;
  onDelete: () => void;
  onViewContacts: () => void;
};

function SortableStepCard({ step, idx, canEdit, formatDelay, onEdit, onDelete, onViewContacts }: SortableStepCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const hasContacts = (step.activeCount || 0) + (step.pausedCount || 0) > 0;

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-4" data-testid={`card-step-${idx}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div
              {...(canEdit ? { ...attributes, ...listeners } : {})}
              className={`flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0 select-none ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
              title={canEdit ? "Drag to reorder" : undefined}
            >
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">
                  {formatDelay(step.delayMinutes)}{step.sendAtTime ? ` at ${step.sendAtTime} CT` : ""}
                </span>
                {step.pauseOnReply && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    <Pause className="w-3 h-3 mr-1" />
                    Pause on reply
                  </Badge>
                )}
                {step.sendAsReply && (
                  <Badge variant="outline" className="text-xs shrink-0" data-testid={`badge-send-as-reply-${idx}`}>
                    Sends as reply
                  </Badge>
                )}
              </div>
              <p className="font-medium" data-testid={`text-step-subject-${idx}`}>{step.subjectTemplate}</p>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{step.bodyTemplate}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="flex flex-col items-end text-sm" data-testid={`step-counts-${idx}`}>
              <div className="flex items-center gap-1">
                <span className="text-green-600 dark:text-green-400 font-medium">{step.activeCount || 0}</span>
                <span className="text-muted-foreground text-xs">active</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-600 dark:text-yellow-400 font-medium">{step.pausedCount || 0}</span>
                <span className="text-muted-foreground text-xs">paused</span>
              </div>
            </div>

            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`btn-step-menu-${idx}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {hasContacts && (
                    <>
                      <DropdownMenuItem onClick={onViewContacts}>
                        <Users className="w-4 h-4 mr-2" />
                        View Contacts ({(step.activeCount || 0) + (step.pausedCount || 0)})
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={onEdit}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export function DripCampaignTab() {
  const { toast } = useToast();
  const [selectedCampaign, setSelectedCampaign] = useState<DripCampaign | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<DripCampaignStep | null>(null);

  // M1: trigger types user can pick from. Order = "most useful first".
  const TRIGGER_TYPES: Array<{ value: string; label: string; configKey?: string; configLabel?: string; configDefault?: number; configHint?: string }> = [
    { value: "manual",                label: "Manual enrollment (no automatic trigger)" },
    { value: "lead.status_changed",   label: "Lead status changed" },
    { value: "lead.inactive",         label: "Lead has been inactive",   configKey: "daysInactive",  configLabel: "After N days of no activity",   configDefault: 30, configHint: "We measure activity from the latest call, email, or note." },
    { value: "show.scheduled",        label: "Show scheduled (upcoming)", configKey: "daysBefore",    configLabel: "Days before the show",          configDefault: 7,  configHint: "Recipient is the booking contact." },
    { value: "show.completed",        label: "Show just completed",       configKey: "daysAfter",     configLabel: "Days after the show",           configDefault: 1 },
    { value: "tour.started",          label: "Multi-show run started" },
    { value: "tour.ended",            label: "Multi-show run ended",      configKey: "daysAfter",     configLabel: "Days after the run ends",       configDefault: 1 },
    // "Deposit paid" is intentionally omitted from M1: in this CRM
    // deposit + balance live on the same invoice row, so there is no
    // distinct deposit-paid signal to fire on yet. Surface it again
    // when split-deposit invoicing is added.
    { value: "booking.balance_paid",  label: "Balance paid in full" },
    { value: "invoice.overdue",       label: "Invoice is overdue",        configKey: "daysOverdue",   configLabel: "Days past due",                  configDefault: 1 },
    { value: "contract.unsigned",     label: "Contract still unsigned",   configKey: "daysSent",      configLabel: "Days since contract was sent",  configDefault: 3, configHint: "Note: requires the e-signature feature to be configured." },
  ];

  const [showAdvancedCreate, setShowAdvancedCreate] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ 
    name: "", 
    fromName: "", 
    fromEmail: "",
    scope: "USER" as "MASTER" | "USER",
    assignedToUserId: null as string | null,
    autoEnrollNewLeads: false,
    autoEnrollDelayMinutes: 0,
    autoEnrollDelayUnit: "minutes" as "minutes" | "days",
    // M1 fields with safe defaults — server defaults match these.
    triggerType: "manual" as string,
    triggerConfigValue: 0 as number, // bound to whichever configKey the trigger declares
    autoPauseOnReply: true,
    autoExitOnBooked: true,
    autoExitOnReplied: true,
    autoExitOnDisqualified: true,
    respectBusinessHours: false,
    businessHoursStartLocal: "09:00",
    businessHoursEndLocal: "17:00",
    sendAsAssignedRep: false,
    sendAsRoundRobin: false,
    replyTrickEnabled: false,
    sendTimeSpreadMinutes: 0,
    // M6: optional audience segment. When non-empty, the polled segment
    // sweep enrolls every matching contact every 5 min.
    targetSegmentEntity: "lead" as "lead" | "client",
    targetSegmentRules: [] as Array<{ field: string; op: string; value: string }>,
    // Task #317 — explicit Warm/Cold outreach choice. Default warm.
    // Carried through to the create payload so the send pipeline routes
    // cold campaigns through the verified cold sending domain/relay.
    riskTier: "warm" as "warm" | "cold",
    // Per-campaign daily send cap. null = no cap.
    maxSendsPerDay: null as number | null,
    // Task #406 — forward prospect replies on managed cold mailboxes to the
    // workspace forward target. Defaults on; only meaningful for cold.
    forwardColdReplies: true,
  });
  // M2: stepType lets a step deliver via SMS instead of email. The other
  // channel's draft is preserved when toggling so creators can flip back
  // and forth without losing work.
  // M3: branching + skip-if filter live alongside the channel state.
  // Empty strings on the branch targets mean "fall through to next step".
  const [newStep, setNewStep] = useState({
    delayMinutes: 0,
    delayUnit: "days",
    stepType: "email" as "email" | "sms",
    subjectTemplate: "",
    bodyTemplate: "",
    smsBody: "",
    pauseOnReply: true,
    sendAsReply: false,
    sendAtTime: "",
    // M3 fields
    branchOnCondition: "" as "" | "opened" | "clicked" | "replied" | "booked",
    branchOnLookbackHours: 24,
    branchTrueGoToStepOrder: "" as string,
    branchFalseGoToStepOrder: "" as string,
    skipIfField: "" as string,
    skipIfOp: "eq" as string,
    skipIfValue: "" as string,
  });
  const [showStepAdvanced, setShowStepAdvanced] = useState(false);
  // Reset payload reused by handleAddStep + the create-success handler so
  // the M2/M3 fields stay in sync if we add more.
  const blankStep = {
    delayMinutes: 0,
    delayUnit: "days",
    stepType: "email" as const,
    subjectTemplate: "",
    bodyTemplate: "",
    smsBody: "",
    pauseOnReply: true,
    sendAsReply: false,
    sendAtTime: "",
    branchOnCondition: "" as "" | "opened" | "clicked" | "replied" | "booked",
    branchOnLookbackHours: 24,
    branchTrueGoToStepOrder: "",
    branchFalseGoToStepOrder: "",
    skipIfField: "",
    skipIfOp: "eq",
    skipIfValue: "",
  };
  const [moveStepEnrollment, setMoveStepEnrollment] = useState<DripEnrollment | null>(null);
  const [moveToStepIndex, setMoveToStepIndex] = useState<number>(0);
  const [viewStepIndex, setViewStepIndex] = useState<number | null>(null);
  const [templateSearch, setTemplateSearch] = useState("");
  
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadRegionFilter, setLeadRegionFilter] = useState<string | null>(null);
  const [leadStatusFilter, setLeadStatusFilter] = useState<string>("all");
  // Which audience the enroll dialog is selecting from. Forced to "client"
  // for warm campaigns; user-toggleable for cold campaigns.
  const [enrollContactType, setEnrollContactType] = useState<"lead" | "client">("lead");
  const [editDelayUnit, setEditDelayUnit] = useState<"minutes" | "days">("minutes");
  const [editDelayValue, setEditDelayValue] = useState(0);
  const [editMaxSends, setEditMaxSends] = useState<string>("");
  const [regionSearchOpen, setRegionSearchOpen] = useState(false);

  // "Edit step(s) & retry failed messages" — when the drip scheduler
  // marks messages failed (typically a leaked merge variable in a step),
  // give the user a single dialog where they edit each failing step
  // once and re-queue every failed message in one shot.
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryStepEdits, setRetryStepEdits] = useState<Record<string, { subjectTemplate: string; bodyTemplate: string }>>({});
  const [retryGuardError, setRetryGuardError] = useState<{ stepId?: string; tokens: string[] } | null>(null);

  const { data: permissions } = useQuery<DripPermissions>({
    queryKey: ["/api/drip-campaigns/permissions"],
  });

  const { data: users = [] } = useQuery<DripUser[]>({
    queryKey: ["/api/drip-campaigns/users"],
  });

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery<DripCampaign[]>({
    queryKey: ["/api/drip-campaigns"],
  });

  const { data: campaignDetails } = useQuery<DripCampaign & { steps: DripCampaignStep[]; enrollments: DripEnrollment[] }>({
    queryKey: ["/api/drip-campaigns", selectedCampaign?.id],
    enabled: !!selectedCampaign?.id,
  });

  const { data: leads = [] } = useQuery<any[]>({
    queryKey: ["/api/leads"],
  });

  const { data: regions = [] } = useQuery<any[]>({
    queryKey: ["/api/regions"],
  });

  // Clients are an enrollable audience too. Warm drips can ONLY enroll
  // clients; cold drips let the user choose leads or clients. This query
  // is workspace-scoped server-side (requirePermission clients.feature.view).
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/clients"],
  });

  interface EmailTemplateLite {
    id: string;
    name: string;
    subject?: string;
    body?: string;
    hiddenFromPickers?: boolean;
  }

  const { data: emailTemplates = [] } = useQuery<EmailTemplateLite[]>({
    queryKey: ["/api/email-templates"],
  });

  const { data: gmailStatus } = useQuery<{ inboundReadingAvailable: boolean; connected: boolean }>({
    queryKey: ["/api/gmail/status"],
    refetchInterval: 5 * 60 * 1000, // re-check every 5 minutes
  });

  const { data: campaignMessages = [] } = useQuery<any[]>({
    queryKey: ["/api/drip-campaigns", selectedCampaign?.id, "messages"],
    enabled: !!selectedCampaign?.id,
  });

  // Task #686 — mirror the cold-tier badge from the Mass Email composer
  // (client/src/pages/email-hub.tsx). Drip campaigns flow through the
  // same cold-send pipeline (cold-send-policy → drip-scheduler) when the
  // audience is leads. We don't have the email-hub audience strings here,
  // but the equivalent signals on a drip campaign are:
  //   - triggerType is a lead.* trigger,
  //   - the optional segment targets the `lead` entity, or
  //   - any current enrollment is for a lead contact.
  // Mirror of `COLD_AUDIENCE_VALUES` in email-hub.tsx — kept as a Set so
  // future additions stay 1:1 with the mass-email composer's heuristic.
  const COLD_DRIP_TRIGGER_PREFIXES = ["lead."];
  const dripUsesColdTier = (() => {
    if (!campaignDetails) return false;
    const trigger = (campaignDetails.triggerType || "").toLowerCase();
    if (COLD_DRIP_TRIGGER_PREFIXES.some(p => trigger.startsWith(p))) return true;
    if ((campaignDetails.targetSegmentEntity || "").toLowerCase() === "lead") return true;
    if (campaignDetails.enrollments?.some(e => (e.contactType || "").toLowerCase() === "lead")) return true;
    return false;
  })();

  type ColdSendingDomain = {
    id: string;
    domain: string;
    fromAddress: string | null;
    spfVerified: boolean;
    dkimVerified: boolean;
    verificationStatus: string;
  };
  const coldSendingDomainQuery = useQuery<{ domain: ColdSendingDomain | null }>({
    queryKey: ["/api/sending-domains/cold"],
    staleTime: 60_000,
    // Task #317 — also needed in the Create dialog to gate the Cold
    // outreach option behind a verified cold sending domain.
    enabled: dripUsesColdTier || isCreateDialogOpen,
  });
  const coldSendingDomain = coldSendingDomainQuery.data?.domain ?? null;
  const coldDomainQueryReady = coldSendingDomainQuery.isSuccess;
  const coldDomainQueryFailed = coldSendingDomainQuery.isError;
  const coldDomainVerified = !!(coldSendingDomain && coldSendingDomain.spfVerified && coldSendingDomain.dkimVerified);

  const myCampaigns = useMemo(() => 
    campaigns.filter(c => c.scope === "USER" && c.ownerUserId === permissions?.userId),
    [campaigns, permissions?.userId]
  );

  const masterCampaigns = useMemo(() => 
    campaigns.filter(c => c.scope === "MASTER"),
    [campaigns]
  );

  const regionMap = useMemo(() => {
    const map = new Map<string, string>();
    regions.forEach((region: any) => {
      map.set(region.id, region.name || region.id);
    });
    return map;
  }, [regions]);

  // Region filter must list EVERY tenant region (workspace-scoped via
  // /api/regions), not just regions that happen to appear on the loaded
  // contacts. Otherwise the dropdown is empty when no leads are loaded
  // (e.g. a warm campaign enrolling clients).
  const uniqueRegions = useMemo(() => {
    return regions
      .map((region: any) => ({ id: region.id as string, name: (region.name || region.id) as string }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [regions]);

  // Warm campaigns enroll clients only; cold campaigns let the user pick.
  const campaignRiskTier: "warm" | "cold" =
    campaignDetails?.riskTier === "cold" ? "cold" : "warm";
  const effectiveEnrollType: "lead" | "client" =
    campaignRiskTier === "warm" ? "client" : enrollContactType;

  // Normalize whichever audience is selected into a common shape so the
  // dialog render + enroll payload stay type-agnostic.
  type EnrollableContact = {
    id: string;
    name: string;
    email: string;
    regionId: string | null;
    status: string | null;
    type: "lead" | "client";
  };
  const enrollableContacts = useMemo<EnrollableContact[]>(() => {
    if (effectiveEnrollType === "client") {
      return clients
        .filter((c: any) => c.emailAddress && !c.emailOptedOut)
        .map((c: any) => ({
          id: c.id,
          name: c.contactName || c.accountName || c.emailAddress,
          email: c.emailAddress,
          regionId: c.regionId || null,
          status: null,
          type: "client" as const,
        }));
    }
    return leads
      .filter((l: any) => l.emailAddress && !l.isSuppressed)
      .map((l: any) => ({
        id: l.id,
        name: l.contactName || l.leadName || l.emailAddress,
        email: l.emailAddress,
        regionId: l.regionId || null,
        status: l.status || null,
        type: "lead" as const,
      }));
  }, [effectiveEnrollType, clients, leads]);

  const filteredContacts = useMemo(() => {
    return enrollableContacts.filter((contact) => {
      if (leadSearchQuery) {
        const query = leadSearchQuery.toLowerCase();
        const matchesSearch =
          contact.name?.toLowerCase().includes(query) ||
          contact.email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      if (leadRegionFilter && contact.regionId !== leadRegionFilter) return false;

      // Lead status filter only applies to leads.
      if (effectiveEnrollType === "lead" && leadStatusFilter !== "all") {
        const status = contact.status?.toLowerCase() || "";
        switch (leadStatusFilter) {
          case "interested":
            if (status !== "interested") return false;
            break;
          case "new":
            if (status && status !== "" && status !== "new") return false;
            break;
          case "not_now":
            if (status !== "not now" && status !== "not_now") return false;
            break;
        }
      }

      return true;
    });
  }, [enrollableContacts, leadSearchQuery, leadRegionFilter, leadStatusFilter, effectiveEnrollType]);

  const createCampaignMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/drip-campaigns", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      setIsCreateDialogOpen(false);
      setShowAdvancedCreate(false);
      setNewCampaign({ 
        name: "", fromName: "", fromEmail: "", 
        scope: "USER", assignedToUserId: null,
        autoEnrollNewLeads: false, autoEnrollDelayMinutes: 0,
        autoEnrollDelayUnit: "minutes",
        triggerType: "manual", triggerConfigValue: 0,
        autoPauseOnReply: true, autoExitOnBooked: true,
        autoExitOnReplied: true, autoExitOnDisqualified: true,
        respectBusinessHours: false, businessHoursStartLocal: "09:00", businessHoursEndLocal: "17:00",
        sendAsAssignedRep: false, sendAsRoundRobin: false,
        replyTrickEnabled: false, sendTimeSpreadMinutes: 0,
        riskTier: "warm",
        targetSegmentEntity: "lead",
        targetSegmentRules: [],
        maxSendsPerDay: null,
        forwardColdReplies: true,
      });
      toast({ title: "Campaign created" });
    },
    onError: () => toast({ title: "Failed to create campaign", variant: "destructive" }),
  });

  // M1: kicks off a self-targeted preview send of every step in the
  // campaign, ~10s apart. Defined here so the button on the campaign
  // detail can call it directly.
  const testSendMutation = useMutation({
    mutationFn: (campaignId: string) => apiRequest("POST", `/api/drip-campaigns/${campaignId}/test-send`),
    onSuccess: async (resp: any) => {
      const data = (resp && typeof resp.json === "function") ? await resp.json() : resp;
      toast({
        title: "Test send started",
        description: data?.message || "Preview emails are on their way to you.",
      });
    },
    onError: (e: any) => toast({ title: e?.message || "Failed to start test send", variant: "destructive" }),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<DripCampaign>) =>
      apiRequest("PUT", `/api/drip-campaigns/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      toast({ title: "Campaign updated" });
    },
  });

  const activateCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/drip-campaigns/${id}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      toast({ title: "Campaign activated", description: "Emails will be sent automatically to enrolled leads." });
    },
    onError: (e: any) => toast({ title: e?.message || "Failed to activate", variant: "destructive" }),
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/drip-campaigns/${id}/pause`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      toast({ title: "Campaign paused" });
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/drip-campaigns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      setSelectedCampaign(null);
      toast({ title: "Campaign deleted" });
    },
  });

  const duplicateCampaignMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/drip-campaigns/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      toast({ title: "Campaign duplicated", description: "A copy has been created as a draft." });
    },
    onError: () => toast({ title: "Failed to duplicate campaign", variant: "destructive" }),
  });

  const createStepMutation = useMutation({
    mutationFn: (data: { campaignId: string; stepOrder: number; delayMinutes: number; stepType?: 'email' | 'sms'; subjectTemplate?: string; bodyTemplate?: string; smsBody?: string | null; pauseOnReply?: boolean; sendAsReply?: boolean; sendAtTime?: string | null }) =>
      apiRequest("POST", `/api/drip-campaigns/${data.campaignId}/steps`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      setIsStepDialogOpen(false);
      setNewStep(blankStep);
      setShowStepAdvanced(false);
      setEditingStep(null);
      toast({ title: "Step added" });
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: ({ stepId, ...data }: { stepId: string; campaignId: string } & Partial<DripCampaignStep>) =>
      apiRequest("PUT", `/api/drip-campaigns/${data.campaignId}/steps/${stepId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      setIsStepDialogOpen(false);
      setEditingStep(null);
      toast({ title: "Step updated" });
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: ({ campaignId, stepId }: { campaignId: string; stepId: string }) =>
      apiRequest("DELETE", `/api/drip-campaigns/${campaignId}/steps/${stepId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      toast({ title: "Step deleted" });
    },
  });

  const retryFailedDripMutation = useMutation({
    mutationFn: ({ campaignId, steps }: { campaignId: string; steps: Array<{ id: string; subject?: string; body?: string }> }) =>
      apiRequest("POST", `/api/drip-campaigns/${campaignId}/retry-failed`, { steps }),
    onSuccess: async (response: any) => {
      const data = await response.json().catch(() => ({}));
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id, "messages"] });
      setRetryDialogOpen(false);
      setRetryStepEdits({});
      setRetryGuardError(null);
      toast({ title: "Retry queued", description: data?.message || "Failed messages re-queued." });
    },
    onError: (error: any) => {
      const { details, title, description } = describeSendError(error, "Failed to retry drip campaign.");
      if (details.isGuardError && details.tokens?.length) {
        setRetryGuardError({ stepId: details.stepId, tokens: details.tokens });
      } else {
        setRetryGuardError(null);
      }
      toast({ title, description, variant: "destructive" });
    },
  });

  const reorderStepsMutation = useMutation({
    mutationFn: ({ campaignId, stepIds }: { campaignId: string; stepIds: string[] }) =>
      apiRequest("PATCH", `/api/drip-campaigns/${campaignId}/steps/reorder`, { stepIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
    },
    onError: () => {
      toast({ title: "Failed to reorder steps", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
    },
  });

  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleStepDragEnd(event: DragEndEvent) {
    if (!campaignDetails || !selectedCampaign) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = campaignDetails.steps.findIndex(s => s.id === active.id);
    const newIndex = campaignDetails.steps.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(campaignDetails.steps, oldIndex, newIndex);
    reorderStepsMutation.mutate({ campaignId: selectedCampaign.id, stepIds: reordered.map(s => s.id) });
  }

  const enrollContactsMutation = useMutation({
    mutationFn: async ({ campaignId, contacts, confirmWarm }: { campaignId: string; contacts: any[]; confirmWarm?: boolean }) => {
      const res = await apiRequest("POST", `/api/drip-campaigns/${campaignId}/enroll`, {
        contacts,
        ...(confirmWarm ? { confirmWarmHighVolume: true } : {}),
      });
      return await res.json();
    },
    onSuccess: (result: any, variables) => {
      // Server-side warm-volume backstop: if the API held the enroll for
      // acknowledgement (e.g. a direct call, or the client gate didn't
      // catch it), surface the checkbox modal instead of finishing.
      if (result?.requiresWarmVolumeConfirmation && !variables.confirmWarm) {
        setEnrollWarmAckOpen(true);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      setIsEnrollDialogOpen(false);
      setSelectedLeadsForEnroll([]);
      setEnrollWarmAckOpen(false);
      toast({ title: `Enrolled ${result.enrolled} (${result.skipped} skipped)` });
    },
  });

  const pauseEnrollmentMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiRequest("POST", `/api/drip-campaigns/enrollments/${enrollmentId}/pause`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      toast({ title: "Enrollment paused" });
    },
  });

  const resumeEnrollmentMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiRequest("POST", `/api/drip-campaigns/enrollments/${enrollmentId}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      toast({ title: "Enrollment resumed" });
    },
  });

  const deleteEnrollmentMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiRequest("DELETE", `/api/drip-campaigns/enrollments/${enrollmentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      toast({ title: "Enrollment removed" });
    },
    onError: () => toast({ title: "Failed to remove enrollment", variant: "destructive" }),
  });

  const moveStepMutation = useMutation({
    mutationFn: ({ enrollmentId, stepIndex }: { enrollmentId: string; stepIndex: number }) =>
      apiRequest("PATCH", `/api/drip-campaigns/enrollments/${enrollmentId}/move-step`, { stepIndex }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id, "messages"] });
      setMoveStepEnrollment(null);
      toast({ title: "Enrollment moved to step" });
    },
    onError: () => toast({ title: "Failed to move enrollment", variant: "destructive" }),
  });

  const forceRetryMutation = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiRequest("POST", `/api/drip-campaigns/enrollments/${enrollmentId}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaign?.id, "messages"] });
      toast({ title: "Retry scheduled", description: "The scheduler will send this within 60 seconds." });
    },
    onError: () => toast({ title: "Failed to retry enrollment", variant: "destructive" }),
  });

  const getStatusBadge = (status: DripCampaign["status"]) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>;
      case "paused":
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Paused</Badge>;
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  const formatDelay = (minutes: number) => {
    if (minutes === 0) return "Immediately";
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) return `${Math.round(minutes / 60)} hours`;
    return `${Math.round(minutes / 1440)} days`;
  };

  const handleAddStep = () => {
    setEditingStep(null);
    setNewStep(blankStep);
    setShowStepAdvanced(false);
    setIsStepDialogOpen(true);
  };

  const handleEditStep = (step: DripCampaignStep) => {
    setEditingStep(step);
    const minutes = step.delayMinutes;
    let unit = "minutes";
    let value = minutes;
    if (minutes >= 1440) {
      unit = "days";
      value = minutes / 1440;
    } else if (minutes >= 60) {
      unit = "hours";
      value = minutes / 60;
    }
    // M3: hydrate branching + skip-if state from the row.
    const rawFilter: any = (step as any).skipIfFilter;
    const filterRow =
      rawFilter && typeof rawFilter === "object" && "field" in rawFilter && "op" in rawFilter
        ? rawFilter
        : null;
    const branchCond = ((step as any).branchOnCondition as string) || "";
    const hasAdvanced =
      !!branchCond ||
      filterRow != null ||
      ((step as any).branchTrueGoToStepOrder != null) ||
      ((step as any).branchFalseGoToStepOrder != null);
    setNewStep({
      delayMinutes: value,
      delayUnit: unit,
      stepType: ((step as any).stepType as 'email' | 'sms') || 'email',
      subjectTemplate: step.subjectTemplate || "",
      bodyTemplate: step.bodyTemplate || "",
      smsBody: ((step as any).smsBody as string) || "",
      pauseOnReply: step.pauseOnReply ?? true,
      sendAsReply: step.sendAsReply ?? false,
      sendAtTime: step.sendAtTime || "",
      branchOnCondition: branchCond as any,
      branchOnLookbackHours: (step as any).branchOnLookbackHours ?? 24,
      branchTrueGoToStepOrder:
        (step as any).branchTrueGoToStepOrder != null
          ? String((step as any).branchTrueGoToStepOrder)
          : "",
      branchFalseGoToStepOrder:
        (step as any).branchFalseGoToStepOrder != null
          ? String((step as any).branchFalseGoToStepOrder)
          : "",
      skipIfField: filterRow?.field || "",
      skipIfOp: filterRow?.op || "eq",
      skipIfValue: filterRow?.value != null ? String(filterRow.value) : "",
    });
    setShowStepAdvanced(hasAdvanced);
    setIsStepDialogOpen(true);
  };

  const handleSaveStep = () => {
    if (!selectedCampaign) return;
    
    let delayMinutes = newStep.delayMinutes;
    if (newStep.delayUnit === "hours") delayMinutes = newStep.delayMinutes * 60;
    if (newStep.delayUnit === "days") delayMinutes = newStep.delayMinutes * 1440;

    // M2: send channel-specific fields. Email steps null out smsBody, SMS
    // steps null out subject/body so the server-side validator sees a clean
    // payload for whichever channel the user picked.
    const baseStepData = newStep.stepType === 'sms'
      ? {
          delayMinutes,
          stepType: 'sms' as const,
          subjectTemplate: '',
          bodyTemplate: '',
          smsBody: newStep.smsBody,
          pauseOnReply: newStep.pauseOnReply,
          sendAsReply: false, // sendAsReply is email-thread specific, ignore for SMS
          sendAtTime: newStep.sendAtTime || null,
        }
      : {
          delayMinutes,
          stepType: 'email' as const,
          subjectTemplate: newStep.subjectTemplate,
          bodyTemplate: newStep.bodyTemplate,
          smsBody: null,
          pauseOnReply: newStep.pauseOnReply,
          sendAsReply: newStep.sendAsReply,
          sendAtTime: newStep.sendAtTime || null,
        };

    // M3: branch + skip-if. Send `null` (not undefined) when cleared so
    // PUT updates actually remove a previously-set value. Skip-if filter
    // is built from the simple field/op/value picker; advanced JSON would
    // overwrite this if we ever add a JSON editor.
    const branchData: any = {};
    if (showStepAdvanced && newStep.branchOnCondition) {
      branchData.branchOnCondition = newStep.branchOnCondition;
      branchData.branchOnLookbackHours = newStep.branchOnLookbackHours || 24;
      branchData.branchTrueGoToStepOrder = newStep.branchTrueGoToStepOrder
        ? Number(newStep.branchTrueGoToStepOrder)
        : null;
      branchData.branchFalseGoToStepOrder = newStep.branchFalseGoToStepOrder
        ? Number(newStep.branchFalseGoToStepOrder)
        : null;
    } else {
      branchData.branchOnCondition = null;
      branchData.branchTrueGoToStepOrder = null;
      branchData.branchFalseGoToStepOrder = null;
    }
    if (showStepAdvanced && newStep.skipIfField) {
      branchData.skipIfFilter = {
        field: newStep.skipIfField,
        op: newStep.skipIfOp,
        value: newStep.skipIfValue,
      };
    } else {
      branchData.skipIfFilter = null;
    }

    const stepData = { ...baseStepData, ...branchData };

    if (editingStep) {
      updateStepMutation.mutate({ stepId: editingStep.id, campaignId: selectedCampaign.id, ...(stepData as any) });
    } else {
      createStepMutation.mutate({ campaignId: selectedCampaign.id, stepOrder: (campaignDetails?.steps?.length || 0) + 1, ...stepData });
    }
  };

  // Detect Gmail issues: either failed messages with credential errors, OR sender has no Gmail connected
  const hasGmailError = useMemo(() => {
    if (!campaignDetails) return false;
    const hasFailedCredentialMsg = campaignMessages.some((m: any) => m.status === 'failed' && m.error?.toLowerCase().includes('credential'));
    const senderMissingGmail = campaignDetails.status === 'active' && campaignDetails.senderHasGmail === false;
    return hasFailedCredentialMsg || senderMissingGmail;
  }, [campaignMessages, campaignDetails]);

  const gmailWarningMessage = useMemo(() => {
    if (!campaignDetails) return null;
    const hasFailedCredentialMsg = campaignMessages.some((m: any) => m.status === 'failed' && m.error?.toLowerCase().includes('credential'));
    if (hasFailedCredentialMsg) {
      return "Gmail credentials expired. Emails are failing to send. Please reconnect your Gmail account in Settings > Integrations to resume sending.";
    }
    if (campaignDetails.status === 'active' && campaignDetails.senderHasGmail === false) {
      return "No Gmail account connected. This campaign cannot send emails until the sender connects their Gmail account in Settings > Integrations.";
    }
    return null;
  }, [campaignMessages, campaignDetails]);

  const [selectedLeadsForEnroll, setSelectedLeadsForEnroll] = useState<string[]>([]);
  const [enrollWarmAckOpen, setEnrollWarmAckOpen] = useState(false);

  const { data: massEmailSettings } = useQuery<MassEmailSettingsLite>({
    queryKey: ["/api/mass-email-settings"],
  });
  const warmVolumeThreshold = Math.max(1, massEmailSettings?.warmVolumeWarnThreshold ?? 20);

  const performEnroll = (confirmWarm: boolean) => {
    if (!selectedCampaign || selectedLeadsForEnroll.length === 0) return;

    const byId = new Map(enrollableContacts.map((c) => [c.id, c]));
    const contacts = selectedLeadsForEnroll
      .map((id) => {
        const c = byId.get(id);
        if (!c) return null;
        return {
          contactId: c.id,
          contactType: c.type,
          contactEmail: c.email || "",
          contactName: c.name || "",
        };
      })
      .filter((c): c is { contactId: string; contactType: "lead" | "client"; contactEmail: string; contactName: string } => !!c && !!c.contactEmail);

    // Selection + modal are cleared in the mutation's onSuccess (only on a
    // real enroll), so a server-side warm-volume hold can re-surface the
    // checkbox with the count intact.
    enrollContactsMutation.mutate({ campaignId: selectedCampaign.id, contacts, confirmWarm });
  };

  // The warm-volume acknowledge gate is enforced server-side (it mirrors the
  // drip scheduler's effective cold/warm routing exactly, so it only holds
  // when the send will truly be warm). We always attempt the enroll without
  // the confirm flag first; if the server holds it, enrollContactsMutation's
  // onSuccess opens the acknowledge modal, whose confirm re-submits with the
  // flag set. Single source of truth — no client-side drift.
  const handleEnroll = () => {
    if (!selectedCampaign || selectedLeadsForEnroll.length === 0) return;
    performEnroll(false);
  };

  const handleCreateCampaign = () => {
    const data: any = {
      name: newCampaign.name,
      fromName: newCampaign.fromName || undefined,
      fromEmail: newCampaign.fromEmail || undefined,
    };
    
    if (permissions?.isAdmin) {
      data.scope = newCampaign.scope;
      if (newCampaign.scope === "MASTER") {
        data.assignedToUserId = newCampaign.assignedToUserId;
        data.autoEnrollNewLeads = newCampaign.autoEnrollNewLeads;
        const delayValue = newCampaign.autoEnrollDelayMinutes || 0;
        data.autoEnrollDelayMinutes = newCampaign.autoEnrollDelayUnit === "days" 
          ? delayValue * 1440 
          : delayValue;
      }
    }

    // M1: pack the simple-mode trigger choice + per-trigger config
    // into the API shape the server expects.
    data.triggerType = newCampaign.triggerType;

    // Task #317 — carry the explicit Warm/Cold choice through to the
    // create payload. Guard: only allow cold when a verified cold
    // sending domain exists (the UI also disables Cold in that case).
    data.riskTier = newCampaign.riskTier === "cold" && coldDomainVerified ? "cold" : "warm";

    // Task #406 — only meaningful for cold campaigns (managed mailboxes).
    // Send the per-campaign forward opt-out alongside the cold choice.
    if (data.riskTier === "cold") {
      data.forwardColdReplies = newCampaign.forwardColdReplies;
    }

    const triggerMeta = TRIGGER_TYPES.find(t => t.value === newCampaign.triggerType);
    if (triggerMeta?.configKey) {
      data.triggerConfig = { [triggerMeta.configKey]: newCampaign.triggerConfigValue || triggerMeta.configDefault || 0 };
    } else {
      data.triggerConfig = {};
    }

    // Advanced fields — only send when the user has actually
    // touched the panel; otherwise let server defaults apply.
    if (showAdvancedCreate) {
      data.autoPauseOnReply = newCampaign.autoPauseOnReply;
      data.autoExitOnBooked = newCampaign.autoExitOnBooked;
      data.autoExitOnReplied = newCampaign.autoExitOnReplied;
      data.autoExitOnDisqualified = newCampaign.autoExitOnDisqualified;
      data.respectBusinessHours = newCampaign.respectBusinessHours;
      data.businessHoursStartLocal = newCampaign.businessHoursStartLocal;
      data.businessHoursEndLocal = newCampaign.businessHoursEndLocal;
      data.sendAsAssignedRep = newCampaign.sendAsAssignedRep;
      data.sendAsRoundRobin = newCampaign.sendAsRoundRobin;
      data.replyTrickEnabled = newCampaign.replyTrickEnabled;
      data.sendTimeSpreadMinutes = newCampaign.sendTimeSpreadMinutes;
      data.maxSendsPerDay = newCampaign.maxSendsPerDay;
      // M6: emit segment only when the user actually built one. An
      // empty rules array means "no segment" — we explicitly send null
      // so an existing segment can be cleared by saving the dialog
      // again with no rules.
      if (newCampaign.targetSegmentRules.length > 0) {
        const cleaned = newCampaign.targetSegmentRules
          .filter((r) => r.field && r.op && r.value !== "")
          .map((r) => {
            // Numeric ops carry numeric values.
            const numericOps = new Set(["gt", "gte", "lt", "lte"]);
            return {
              field: r.field,
              op: r.op,
              value: numericOps.has(r.op) ? Number(r.value) : r.value,
            };
          });
        if (cleaned.length > 0) {
          data.targetSegmentEntity = newCampaign.targetSegmentEntity;
          data.targetSegmentJson = cleaned.length === 1 ? cleaned[0] : { all: cleaned };
        }
      }
    }

    createCampaignMutation.mutate(data);
  };

  const handleSelectCampaign = (campaign: DripCampaign) => {
    setSelectedCampaign(campaign);
    const delayMinutes = campaign.autoEnrollDelayMinutes || 0;
    if (delayMinutes >= 1440 && delayMinutes % 1440 === 0) {
      setEditDelayUnit("days");
      setEditDelayValue(delayMinutes / 1440);
    } else {
      setEditDelayUnit("minutes");
      setEditDelayValue(delayMinutes);
    }
    setEditMaxSends(campaign.maxSendsPerDay != null ? String(campaign.maxSendsPerDay) : "");
  };

  const canEditCampaign = (campaign: DripCampaign) => {
    if (permissions?.isAdmin) return true;
    return campaign.scope === "USER" && campaign.ownerUserId === permissions?.userId;
  };

  if (!permissions?.canViewCampaigns) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">You don't have permission to view drip campaigns.</p>
      </Card>
    );
  }

  if (selectedCampaign && campaignDetails) {
    const canEdit = canEditCampaign(campaignDetails);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedCampaign(null)} data-testid="btn-back-campaigns">
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Back to Campaigns
          </Button>
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold" data-testid="text-campaign-name">{campaignDetails.name}</h2>
              {getStatusBadge(campaignDetails.status)}
              {campaignDetails.scope === "MASTER" && (
                <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                  <Crown className="w-3 h-3 mr-1" />
                  Master
                </Badge>
              )}
              {(campaignDetails as any).maxSendsPerDay != null && (
                <Badge variant="secondary" className="text-xs font-normal" data-testid="badge-detail-daily-cap">
                  Today: {(selectedCampaign as any)?.sentToday ?? 0}/{(campaignDetails as any).maxSendsPerDay} sent
                </Badge>
              )}
            </div>
            {campaignDetails.fromEmail && (
              <p className="text-sm text-muted-foreground mt-1">
                From: {campaignDetails.fromName ? `${campaignDetails.fromName} <${campaignDetails.fromEmail}>` : campaignDetails.fromEmail}
              </p>
            )}
            {/* Task #686 / cold-email vision — Admin-only cold-tier badge.
                Reps don't see "cold tier", domains, or caps — admin policy
                and the cold-domain plumbing decide routing invisibly. Pre-
                flight will surface a friendly error at send time if the
                cold domain isn't ready. */}
            {dripUsesColdTier && permissions?.isAdmin && (
              <div className="mt-2 flex items-start gap-2" data-testid="cold-tier-domain-badge">
                {!coldDomainQueryReady && !coldDomainQueryFailed ? (
                  <Badge variant="outline" className="text-xs font-normal" data-testid="badge-cold-domain-loading">
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                    Cold tier — checking sending domain…
                  </Badge>
                ) : coldDomainQueryFailed ? (
                  <Badge variant="outline" className="text-xs font-normal" data-testid="badge-cold-domain-error">
                    <AlertCircle className="w-3 h-3 mr-1.5" />
                    Cold tier — couldn't check sending domain. Try again in a moment.
                  </Badge>
                ) : coldDomainVerified ? (
                  <Badge variant="secondary" className="text-xs font-normal" data-testid="badge-cold-domain-verified">
                    <ShieldCheck className="w-3 h-3 mr-1.5" />
                    Cold tier — sending from{" "}
                    <code className="ml-1 font-mono" data-testid="text-cold-domain">
                      {coldSendingDomain!.domain}
                    </code>
                    {coldSendingDomain!.fromAddress && (
                      <span className="ml-1 text-muted-foreground">
                        (as <code className="font-mono">{coldSendingDomain!.fromAddress}</code>)
                      </span>
                    )}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="text-xs font-normal" data-testid="badge-cold-domain-missing">
                    <AlertCircle className="w-3 h-3 mr-1.5" />
                    Cold tier — no verified sending domain.{" "}
                    <Link
                      href="/admin?section=emailSettings&tab=cold&byod=1"
                      className="ml-1 underline"
                      data-testid="link-cold-domain-setup"
                    >
                      Set one up in Email settings
                    </Link>
                  </Badge>
                )}
              </div>
            )}
            {campaignRiskTier === "warm" && (
              <div
                className="mt-2 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-700/60 dark:bg-yellow-950/40 dark:text-yellow-300"
                data-testid="notice-warm-drip"
              >
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Warm drip campaign — client records only.{" "}
                  {permissions?.isAdmin ? (
                    <Link
                      href="/admin?section=emailSettings&tab=cold&byod=1"
                      className="underline font-medium"
                      data-testid="link-cold-admin-panel"
                    >
                      Cold campaigns need to be configured in the admin panel
                    </Link>
                  ) : (
                    <span data-testid="text-cold-admin-panel">
                      Cold campaigns need to be configured in the admin panel
                    </span>
                  )}
                </span>
              </div>
            )}
            {campaignDetails.scope === "MASTER" && campaignDetails.assignedToUserId && (
              <p className="text-sm text-muted-foreground mt-1">
                Assigned to: {campaignDetails.assignedToUserId === "ALL_SALES_REPS" 
                  ? "All Sales Reps" 
                  : users.find(u => u.id === campaignDetails.assignedToUserId)?.name || campaignDetails.assignedToUserId}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && (campaignDetails.steps?.length || 0) > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => testSendMutation.mutate(campaignDetails.id)}
                disabled={testSendMutation.isPending}
                title="Send each step to your own email so you can preview the campaign"
                data-testid="btn-test-send-campaign"
              >
                <Mail className="w-4 h-4 mr-1" />
                {testSendMutation.isPending ? "Sending..." : "Test send to me"}
              </Button>
            )}
            {permissions?.canPauseCampaigns && campaignDetails.status === "active" && canEdit && (
              <Button variant="outline" size="sm" onClick={() => pauseCampaignMutation.mutate(campaignDetails.id)} data-testid="btn-pause-campaign">
                <Pause className="w-4 h-4 mr-1" />
                Pause
              </Button>
            )}
            {canEdit && campaignDetails.status !== "active" && (
              <Button 
                size="sm" 
                onClick={() => {
                  if (confirm("Activate this campaign?\n\nOnce activated, emails will be sent automatically to enrolled leads based on the configured schedule. You can pause the campaign at any time.")) {
                    activateCampaignMutation.mutate(campaignDetails.id);
                  }
                }} 
                disabled={!campaignDetails.steps || campaignDetails.steps.length === 0}
                data-testid="btn-activate-campaign"
              >
                <Play className="w-4 h-4 mr-1" />
                Activate
              </Button>
            )}
            {canEdit && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => {
                  if (confirm("Delete this campaign?")) deleteCampaignMutation.mutate(campaignDetails.id);
                }}
                data-testid="btn-delete-campaign"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {hasGmailError && gmailWarningMessage && (
          <Alert variant="destructive" className="border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 !text-amber-600" />
            <AlertDescription>
              <strong>Gmail not connected.</strong>{" "}{gmailWarningMessage}
            </AlertDescription>
          </Alert>
        )}

        {permissions?.isAdmin && campaignDetails.scope === "MASTER" && (
          <Card className="p-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
            <div className="space-y-4">
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Auto-Enrollment Settings</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={campaignDetails.autoEnrollNewLeads || false}
                    onCheckedChange={(checked) => {
                      updateCampaignMutation.mutate({ 
                        id: campaignDetails.id, 
                        autoEnrollNewLeads: checked 
                      });
                    }}
                    data-testid="switch-auto-enroll"
                  />
                  <Label>Auto-enroll new leads</Label>
                </div>
                {campaignDetails.autoEnrollNewLeads && (
                  <div className="flex items-center gap-2">
                    <Label>Delay:</Label>
                    <Input 
                      type="number" 
                      min="0"
                      className="w-20"
                      value={editDelayValue}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDelayValue(value);
                        const minutes = editDelayUnit === "days" ? value * 1440 : value;
                        updateCampaignMutation.mutate({ 
                          id: campaignDetails.id, 
                          autoEnrollDelayMinutes: minutes 
                        });
                      }}
                      data-testid="input-auto-enroll-delay"
                    />
                    <Select
                      value={editDelayUnit}
                      onValueChange={(value: "minutes" | "days") => {
                        setEditDelayUnit(value);
                        const minutes = value === "days" ? editDelayValue * 1440 : editDelayValue;
                        updateCampaignMutation.mutate({ 
                          id: campaignDetails.id, 
                          autoEnrollDelayMinutes: minutes 
                        });
                      }}
                    >
                      <SelectTrigger className="w-28" data-testid="select-auto-enroll-delay-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes">minutes</SelectItem>
                        <SelectItem value="days">days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                When enabled, new leads will automatically be enrolled in this campaign after the specified delay.
              </p>
            </div>
          </Card>
        )}

        {canEdit && (
          <Card className="p-4">
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Daily Send Cap</h3>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  className="w-28"
                  placeholder="No cap"
                  value={editMaxSends}
                  onChange={(e) => setEditMaxSends(e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const val = raw ? Math.max(1, parseInt(raw) || 1) : null;
                    const current = (campaignDetails as any).maxSendsPerDay ?? null;
                    if (val !== current) {
                      updateCampaignMutation.mutate({ id: campaignDetails.id, maxSendsPerDay: val } as any);
                    }
                    setEditMaxSends(val != null ? String(val) : "");
                  }}
                  data-testid="input-detail-max-sends-per-day"
                />
                <span className="text-sm text-muted-foreground">emails/day max (blank = no limit)</span>
              </div>
              <p className="text-xs text-muted-foreground">
                When the cap is reached, remaining enrollments are deferred until the next UTC day — they are never dropped.
              </p>
            </div>
          </Card>
        )}

        {canEdit && campaignDetails?.riskTier === "cold" && (
          <Card className="p-4" data-testid="card-detail-forward-cold-replies">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <h3 className="font-medium text-sm">Forward replies to a person</h3>
                <p className="text-xs text-muted-foreground">
                  When a prospect replies on a managed (Lease/Buy) cold mailbox, forward a copy to your workspace forward target. Set the destination in Email → Cold Email. Does not apply to bring-your-own mailboxes.
                </p>
              </div>
              <Switch
                checked={(campaignDetails as any).forwardColdReplies ?? true}
                onCheckedChange={(v) =>
                  updateCampaignMutation.mutate({ id: campaignDetails.id, forwardColdReplies: v } as any)
                }
                data-testid="switch-detail-forward-cold-replies"
              />
            </div>
          </Card>
        )}

        <Tabs defaultValue="steps" className="space-y-4">
          <TabsList>
            <TabsTrigger value="steps" data-testid="tab-campaign-steps">Steps ({campaignDetails.steps?.length || 0})</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-campaign-enrollments">
              Enrollments ({campaignDetails.enrollments?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-campaign-analytics">
              Analytics
            </TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="space-y-4">
            <DripAnalyticsPanel campaignId={selectedCampaign.id} />
          </TabsContent>

          <TabsContent value="steps" className="space-y-4">
            {canEdit && (
              <Button onClick={handleAddStep} data-testid="btn-add-step">
                <Plus className="w-4 h-4 mr-2" />
                Add Step
              </Button>
            )}

            {/* Failed-messages banner. Lets the user open the retry dialog,
                edit the failing step content once, and re-queue every
                failed message in the campaign. */}
            {(() => {
              const failedMsgs = campaignMessages.filter((m: any) => m.status === "failed");
              if (failedMsgs.length === 0) return null;
              const failingStepIds = Array.from(
                new Set(failedMsgs.map((m: any) => m.stepId).filter(Boolean)),
              ) as string[];
              const sample = failedMsgs[failedMsgs.length - 1];
              return (
                <div
                  className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-start justify-between gap-3"
                  data-testid="banner-drip-failed"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">
                      {failedMsgs.length} message{failedMsgs.length === 1 ? "" : "s"} failed across{" "}
                      {failingStepIds.length} step{failingStepIds.length === 1 ? "" : "s"}.
                    </p>
                    {sample?.error && (
                      <p className="text-xs text-muted-foreground break-words" data-testid="text-drip-failure-reason">
                        {sample.error}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const seed: Record<string, { subjectTemplate: string; bodyTemplate: string }> = {};
                        for (const id of failingStepIds) {
                          const step = campaignDetails.steps?.find((s) => s.id === id);
                          if (step) {
                            seed[id] = {
                              subjectTemplate: step.subjectTemplate,
                              bodyTemplate: step.bodyTemplate,
                            };
                          }
                        }
                        setRetryStepEdits(seed);
                        setRetryGuardError(null);
                        setRetryDialogOpen(true);
                      }}
                      data-testid="button-edit-retry-drip"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Edit step{failingStepIds.length === 1 ? "" : "s"} & retry failed
                    </Button>
                  )}
                </div>
              );
            })()}

            {(!campaignDetails.steps || campaignDetails.steps.length === 0) ? (
              <Card className="p-8 text-center">
                <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No steps configured yet. Add your first email step to get started.</p>
              </Card>
            ) : (
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
                <SortableContext items={campaignDetails.steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {campaignDetails.steps.map((step, idx) => (
                      <SortableStepCard
                        key={step.id}
                        step={step}
                        idx={idx}
                        canEdit={canEdit}
                        formatDelay={formatDelay}
                        onEdit={() => handleEditStep(step)}
                        onDelete={() => deleteStepMutation.mutate({ campaignId: selectedCampaign.id, stepId: step.id })}
                        onViewContacts={() => setViewStepIndex(idx)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </TabsContent>

          <TabsContent value="enrollments" className="space-y-4">
            {permissions?.canEnrollContacts && canEdit && (
              <Button
                onClick={() => {
                  // Warm campaigns enroll clients only; cold defaults to leads.
                  setEnrollContactType(campaignRiskTier === "warm" ? "client" : "lead");
                  setSelectedLeadsForEnroll([]);
                  setIsEnrollDialogOpen(true);
                }}
                data-testid="btn-enroll-leads"
              >
                <Users className="w-4 h-4 mr-2" />
                Enroll
              </Button>
            )}

            {(!campaignDetails.enrollments || campaignDetails.enrollments.length === 0) ? (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No leads enrolled in this campaign yet.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {campaignDetails.enrollments.map((enrollment) => {
                  const allFailedMsgs = campaignMessages.filter((m: any) => 
                    m.enrollmentId === enrollment.id && m.status === 'failed'
                  );
                  const lastFailedMsg = allFailedMsgs[allFailedMsgs.length - 1];
                  return (
                    <Card key={enrollment.id} className="p-3" data-testid={`card-enrollment-${enrollment.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{enrollment.contactName || enrollment.contactEmail}</p>
                          <p className="text-sm text-muted-foreground">{enrollment.contactEmail}</p>
                          {enrollment.nextSendAt && enrollment.status === "active" && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Next send: {new Date(enrollment.nextSendAt).toLocaleString('en-US', { timeZone: getWorkspaceTimeZone(), month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} {getTimeZoneAbbreviation(enrollment.nextSendAt)}
                            </p>
                          )}
                          {lastFailedMsg && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 shrink-0" />
                              Send failed: {lastFailedMsg.error || "Unknown error"} — use Force Retry to try again
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground text-xs">
                              {campaignDetails.steps && campaignDetails.steps[enrollment.currentStepIndex]
                                ? `Step ${enrollment.currentStepIndex + 1}: ${campaignDetails.steps[enrollment.currentStepIndex].subjectTemplate.slice(0, 20)}...`
                                : `Step ${enrollment.currentStepIndex + 1}`}
                            </p>
                            <Badge variant={
                              enrollment.status === "active" ? "default" :
                              enrollment.status === "completed" ? "secondary" :
                              enrollment.status === "paused" ? "outline" : "destructive"
                            } className="mt-0.5">
                              {enrollment.status}
                            </Badge>
                            {/* Open/click tracking badges */}
                            {((enrollment as any).sentCount > 0 || (enrollment as any).opens > 0 || (enrollment as any).clicks > 0) && (
                              <div className="flex items-center justify-end gap-1 mt-1">
                                {(enrollment as any).sentCount > 0 && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {(enrollment as any).sentCount} sent
                                  </span>
                                )}
                                {(enrollment as any).opens > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-500/10 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">
                                    👁 {(enrollment as any).opens}
                                  </span>
                                )}
                                {(enrollment as any).clicks > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-purple-500/10 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">
                                    🔗 {(enrollment as any).clicks}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {canEdit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`btn-enrollment-menu-${enrollment.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {permissions?.canPauseCampaigns && enrollment.status === "active" && (
                                  <DropdownMenuItem onClick={() => pauseEnrollmentMutation.mutate(enrollment.id)}>
                                    <Pause className="w-4 h-4 mr-2" />
                                    Pause
                                  </DropdownMenuItem>
                                )}
                                {permissions?.canPauseCampaigns && enrollment.status === "paused" && (
                                  <DropdownMenuItem onClick={() => resumeEnrollmentMutation.mutate(enrollment.id)}>
                                    <Play className="w-4 h-4 mr-2" />
                                    Resume
                                  </DropdownMenuItem>
                                )}
                                {campaignDetails.steps && campaignDetails.steps.length > 1 && (
                                  <DropdownMenuItem onClick={() => {
                                    setMoveStepEnrollment(enrollment);
                                    setMoveToStepIndex(enrollment.currentStepIndex);
                                  }}>
                                    <MoveRight className="w-4 h-4 mr-2" />
                                    Move to Step
                                  </DropdownMenuItem>
                                )}
                                {(enrollment.status === "active" || lastFailedMsg) && (
                                  <DropdownMenuItem onClick={() => forceRetryMutation.mutate(enrollment.id)}>
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Force Retry Now
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => {
                                    if (confirm(`Remove ${enrollment.contactName || enrollment.contactEmail} from this campaign?`)) {
                                      deleteEnrollmentMutation.mutate(enrollment.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove Enrollment
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View Step Contacts Dialog */}
        <Dialog open={viewStepIndex !== null} onOpenChange={(open) => { if (!open) setViewStepIndex(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {viewStepIndex !== null && campaignDetails.steps?.[viewStepIndex]
                  ? `Step ${viewStepIndex + 1}: ${campaignDetails.steps[viewStepIndex].subjectTemplate}`
                  : "Contacts at Step"}
              </DialogTitle>
              <DialogDescription>
                Contacts currently waiting at this step. Force send to trigger immediately, change step to move them, or remove to unenroll.
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {viewStepIndex !== null && campaignDetails.enrollments && (() => {
                const stepEnrollments = campaignDetails.enrollments.filter(
                  e => e.currentStepIndex === viewStepIndex && (e.status === 'active' || e.status === 'paused')
                );
                if (stepEnrollments.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No contacts at this step</p>
                    </div>
                  );
                }
                return stepEnrollments.map((enrollment) => {
                  const allFailedMsgs = campaignMessages.filter((m: any) =>
                    m.enrollmentId === enrollment.id && m.status === 'failed'
                  );
                  const lastFailedMsg = allFailedMsgs[allFailedMsgs.length - 1];
                  return (
                    <div key={enrollment.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{enrollment.contactName || enrollment.contactEmail}</p>
                        <p className="text-xs text-muted-foreground truncate">{enrollment.contactEmail}</p>
                        {enrollment.nextSendAt && enrollment.status === 'active' && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Next: {new Date(enrollment.nextSendAt).toLocaleString('en-US', { timeZone: getWorkspaceTimeZone(), month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} {getTimeZoneAbbreviation(enrollment.nextSendAt)}
                          </p>
                        )}
                        {lastFailedMsg && (
                          <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 shrink-0" />
                            {lastFailedMsg.error || "Send failed"}
                          </p>
                        )}
                        <Badge variant={enrollment.status === 'active' ? 'default' : 'outline'} className="mt-1 text-xs">
                          {enrollment.status}
                        </Badge>
                        {((enrollment as any).opens > 0 || (enrollment as any).clicks > 0) && (
                          <div className="flex items-center gap-1 mt-1">
                            {(enrollment as any).opens > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-500/10 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">
                                👁 {(enrollment as any).opens}
                              </span>
                            )}
                            {(enrollment as any).clicks > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] bg-purple-500/10 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">
                                🔗 {(enrollment as any).clicks}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2"
                          title="Force Send Now"
                          onClick={() => forceRetryMutation.mutate(enrollment.id)}
                          disabled={forceRetryMutation.isPending}
                          data-testid={`btn-force-send-${enrollment.id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Send Now
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`btn-step-contact-menu-${enrollment.id}`}>
                              <MoreVertical className="w-3.5 h-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {campaignDetails.steps && campaignDetails.steps.length > 1 && (
                              <DropdownMenuItem onClick={() => {
                                setViewStepIndex(null);
                                setMoveStepEnrollment(enrollment);
                                setMoveToStepIndex(enrollment.currentStepIndex);
                              }}>
                                <MoveRight className="w-4 h-4 mr-2" />
                                Change Step
                              </DropdownMenuItem>
                            )}
                            {enrollment.status === 'active' && (
                              <DropdownMenuItem onClick={() => pauseEnrollmentMutation.mutate(enrollment.id)}>
                                <Pause className="w-4 h-4 mr-2" />
                                Pause
                              </DropdownMenuItem>
                            )}
                            {enrollment.status === 'paused' && (
                              <DropdownMenuItem onClick={() => resumeEnrollmentMutation.mutate(enrollment.id)}>
                                <Play className="w-4 h-4 mr-2" />
                                Resume
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                if (confirm(`Remove ${enrollment.contactName || enrollment.contactEmail} from this campaign?`)) {
                                  deleteEnrollmentMutation.mutate(enrollment.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isStepDialogOpen} onOpenChange={setIsStepDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingStep ? "Edit Step" : "Add Step"}</DialogTitle>
              <DialogDescription>
                {newStep.stepType === 'sms'
                  ? "Configure the SMS step for this drip campaign."
                  : "Configure the email step for this drip campaign."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto pr-1">
              {/* M2: channel toggle. Email = subject + body editor + Gmail
                  features (load template, AI draft, send-as-reply). SMS = a
                  single 1600-char body with a character counter. */}
              <div className="space-y-2">
                <Label>Channel</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={newStep.stepType === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewStep({ ...newStep, stepType: 'email' })}
                    data-testid="btn-step-channel-email"
                    className="flex-1"
                  >
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={newStep.stepType === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewStep({ ...newStep, stepType: 'sms' })}
                    data-testid="btn-step-channel-sms"
                    className="flex-1"
                  >
                    SMS
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  SMS steps use the contact's phone number and the workspace's SMS provider. Subject lines and email-only options don't apply.
                </p>
              </div>

              {newStep.stepType === 'email' && emailTemplates.filter((t) => !t.hiddenFromPickers).length > 0 && (
                <div className="space-y-2">
                  <Label>Load from Template (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search templates..."
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      className="flex-1"
                      data-testid="input-template-search"
                    />
                  </div>
                  <div className="max-h-28 overflow-y-auto border rounded-md divide-y">
                    {emailTemplates
                      // Task #696: drop picker-hidden templates from the
                      // drip campaign step builder. Drip schedulers resolve
                      // by id once a step is created, so already-saved hidden
                      // templates continue to fire.
                      .filter((t) => !t.hiddenFromPickers)
                      .filter((t: any) => !templateSearch || t.name?.toLowerCase().includes(templateSearch.toLowerCase()) || t.subject?.toLowerCase().includes(templateSearch.toLowerCase()))
                      .slice(0, 10)
                      .map((template: any) => (
                        <button
                          key={template.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                          onClick={() => {
                            setNewStep({ ...newStep, subjectTemplate: template.subject || "", bodyTemplate: template.body || template.htmlBody || "" });
                            setTemplateSearch("");
                          }}
                          data-testid={`btn-template-${template.id}`}
                        >
                          <span className="font-medium">{template.name}</span>
                          {template.subject && <span className="text-muted-foreground ml-2 text-xs">— {template.subject.slice(0, 40)}</span>}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Delay</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={newStep.delayMinutes}
                    onChange={(e) => setNewStep({ ...newStep, delayMinutes: parseInt(e.target.value) || 0 })}
                    className="w-24"
                    data-testid="input-step-delay"
                  />
                  <Select value={newStep.delayUnit} onValueChange={(v) => setNewStep({ ...newStep, delayUnit: v })}>
                    <SelectTrigger className="w-32" data-testid="select-step-delay-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">Time to wait before sending this email (from enrollment or previous step)</p>
              </div>

              <div className="space-y-2">
                <Label>Send at time (CT, optional)</Label>
                <Input
                  type="time"
                  value={newStep.sendAtTime}
                  onChange={(e) => setNewStep({ ...newStep, sendAtTime: e.target.value })}
                  className="w-36"
                  data-testid="input-step-send-at-time"
                />
                <p className="text-xs text-muted-foreground">
                  If set, the email will be sent at this exact time ({getTimeZoneAbbreviation()}) on the target day, rather than exactly after the delay.
                </p>
              </div>

              {newStep.stepType === 'email' && (
                <>
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      value={newStep.subjectTemplate}
                      onChange={(e) => setNewStep({ ...newStep, subjectTemplate: e.target.value })}
                      placeholder="Welcome to our newsletter, {{firstName}}!"
                      data-testid="input-step-subject"
                    />
                    <p className="text-xs text-muted-foreground">Use {"{{firstName}}"}, {"{{lastName}}"}, {"{{email}}"}, {"{{region}}"}, {"{{region_dates}}"} for personalization</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Body</Label>
                      <AIDraftButton
                        kind="drip"
                        context={`Subject: ${newStep.subjectTemplate || "(none yet)"}\nCurrent body:\n${newStep.bodyTemplate || "(empty)"}`}
                        onDraft={(text) => setNewStep((s) => ({ ...s, bodyTemplate: text }))}
                      />
                    </div>
                    <Textarea
                      value={newStep.bodyTemplate}
                      onChange={(e) => setNewStep({ ...newStep, bodyTemplate: e.target.value })}
                      placeholder="Hi {{firstName}}, we're excited to have you..."
                      className="min-h-32"
                      data-testid="input-step-body"
                    />
                  </div>
                </>
              )}

              {newStep.stepType === 'sms' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Message</Label>
                    {(() => {
                      // Show a 160-char counter so creators know when their
                      // message will overflow into multiple SMS segments.
                      const len = (newStep.smsBody || '').length;
                      const segments = Math.max(1, Math.ceil(len / 160));
                      const overSingle = len > 160;
                      return (
                        <span
                          className={cn("text-xs", overSingle ? "text-amber-600" : "text-muted-foreground")}
                          data-testid="text-sms-char-count"
                        >
                          {len}/160 chars · {segments} segment{segments === 1 ? '' : 's'}
                        </span>
                      );
                    })()}
                  </div>
                  <Textarea
                    value={newStep.smsBody}
                    onChange={(e) => setNewStep({ ...newStep, smsBody: e.target.value.slice(0, 1600) })}
                    placeholder="Hi {{firstName}}, just checking in — let me know if you have any questions!"
                    className="min-h-24"
                    maxLength={1600}
                    data-testid="input-step-sms-body"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use {"{{firstName}}"}, {"{{lastName}}"}, {"{{email}}"} for personalization. Carriers split messages over 160 characters into multiple billed segments.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/50">
                <Switch 
                  checked={newStep.pauseOnReply}
                  onCheckedChange={(checked) => setNewStep({ ...newStep, pauseOnReply: checked })}
                  data-testid="switch-pause-on-reply"
                />
                <div>
                  <Label className="cursor-pointer">Pause if lead responds</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {newStep.stepType === 'sms'
                      ? "Automatically pause the campaign for this lead if they text back."
                      : "Automatically pause the campaign for this lead if they reply to this email."}
                  </p>
                </div>
              </div>

              {newStep.stepType === 'email' && (() => {
                // Step 1 has no previous step to chain on, so the toggle is disabled.
                // - Editing an existing step → use its actual position (stepOrder is 1-based).
                // - Adding a new step → it will be appended at end (existing length + 1).
                const stepPosition = editingStep
                  ? editingStep.stepOrder
                  : (campaignDetails?.steps?.length || 0) + 1;
                const isFirstStep = stepPosition <= 1;
                const baseSubject = newStep.subjectTemplate || "(no subject)";
                const previewReSubject = /^re:\s*/i.test(baseSubject) ? baseSubject : `Re: ${baseSubject}`;
                return (
                  <div
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-md border bg-muted/50",
                      isFirstStep && "opacity-60",
                    )}
                  >
                    <Switch
                      checked={!isFirstStep && newStep.sendAsReply}
                      disabled={isFirstStep}
                      onCheckedChange={(checked) => setNewStep({ ...newStep, sendAsReply: checked })}
                      data-testid="switch-send-as-reply"
                    />
                    <div className="flex-1">
                      <Label className={cn("cursor-pointer", isFirstStep && "cursor-not-allowed")}>
                        Send as reply to previous step
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {isFirstStep
                          ? "Step 1 starts a new email thread, so there is nothing to reply to. Add a later step to use this option."
                          : 'Chains this email into the same Gmail thread as the previous step. Subject is automatically prefixed with "Re:".'}
                      </p>
                      {!isFirstStep && newStep.sendAsReply && (
                        <p
                          className="text-xs text-muted-foreground mt-1"
                          data-testid="text-send-as-reply-subject-preview"
                        >
                          Recipients will see: <span className="font-mono">{previewReSubject}</span>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* M3: Advanced — branching + skip-if filter */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showStepAdvanced}
                    onCheckedChange={setShowStepAdvanced}
                    data-testid="switch-show-step-advanced"
                  />
                  <Label className="cursor-pointer">Show advanced options</Label>
                </div>
                {showStepAdvanced && (
                  <div className="mt-4 space-y-5">
                    {/* M5: A/B variants — only meaningful for an existing step
                        (we need its id to attach variant rows). */}
                    {editingStep && selectedCampaign && (
                      <DripStepVariantsEditor
                        campaignId={selectedCampaign.id}
                        stepId={editingStep.id}
                        isEmail={(newStep.stepType || "email") === "email"}
                      />
                    )}
                    {/* Branching */}
                    <div className="space-y-3 p-3 rounded-md border bg-muted/30">
                      <div>
                        <Label className="text-sm font-semibold">Branching</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          After this step sends, route enrollees to a specific next step based on what they did. Leave the condition blank to fall through normally.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">If recipient...</Label>
                          <Select
                            value={newStep.branchOnCondition || "_none"}
                            onValueChange={(v) =>
                              setNewStep({
                                ...newStep,
                                branchOnCondition: (v === "_none" ? "" : v) as any,
                              })
                            }
                          >
                            <SelectTrigger data-testid="select-branch-condition">
                              <SelectValue placeholder="(no branching)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">(no branching)</SelectItem>
                              <SelectItem value="opened">Opened the email</SelectItem>
                              <SelectItem value="clicked">Clicked a link</SelectItem>
                              <SelectItem value="replied">Replied</SelectItem>
                              <SelectItem value="booked">Was booked</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">...within (hours)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={720}
                            value={newStep.branchOnLookbackHours}
                            onChange={(e) =>
                              setNewStep({
                                ...newStep,
                                branchOnLookbackHours: Math.max(1, Number(e.target.value) || 24),
                              })
                            }
                            disabled={!newStep.branchOnCondition}
                            data-testid="input-branch-lookback-hours"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Then go to step (true)</Label>
                          <Input
                            type="number"
                            min={1}
                            placeholder="next step"
                            value={newStep.branchTrueGoToStepOrder}
                            onChange={(e) =>
                              setNewStep({
                                ...newStep,
                                branchTrueGoToStepOrder: e.target.value,
                              })
                            }
                            disabled={!newStep.branchOnCondition}
                            data-testid="input-branch-true-step"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Otherwise go to step (false)</Label>
                          <Input
                            type="number"
                            min={1}
                            placeholder="next step"
                            value={newStep.branchFalseGoToStepOrder}
                            onChange={(e) =>
                              setNewStep({
                                ...newStep,
                                branchFalseGoToStepOrder: e.target.value,
                              })
                            }
                            disabled={!newStep.branchOnCondition}
                            data-testid="input-branch-false-step"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Skip-if filter */}
                    <div className="space-y-3 p-3 rounded-md border bg-muted/30">
                      <div>
                        <Label className="text-sm font-semibold">Skip if...</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Skip this step entirely when the contact matches a condition (e.g. lead status is already booked).
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={newStep.skipIfField || "_none"}
                          onValueChange={(v) =>
                            setNewStep({ ...newStep, skipIfField: v === "_none" ? "" : v })
                          }
                        >
                          <SelectTrigger data-testid="select-skip-field">
                            <SelectValue placeholder="(no filter)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">(no filter)</SelectItem>
                            <SelectItem value="lead.status">Lead status</SelectItem>
                            <SelectItem value="lead.tags">Lead tags</SelectItem>
                            <SelectItem value="lead.source">Lead source</SelectItem>
                            <SelectItem value="lead.priority">Lead priority</SelectItem>
                            <SelectItem value="lead.assignedTo">Lead assigned to</SelectItem>
                            <SelectItem value="client.totalSpend">Client total spend</SelectItem>
                            <SelectItem value="client.region">Client region</SelectItem>
                            <SelectItem value="client.tags">Client tags</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={newStep.skipIfOp}
                          onValueChange={(v) => setNewStep({ ...newStep, skipIfOp: v })}
                        >
                          <SelectTrigger
                            disabled={!newStep.skipIfField}
                            data-testid="select-skip-op"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="eq">equals</SelectItem>
                            <SelectItem value="neq">does not equal</SelectItem>
                            <SelectItem value="contains">contains</SelectItem>
                            <SelectItem value="notContains">does not contain</SelectItem>
                            <SelectItem value="gt">greater than</SelectItem>
                            <SelectItem value="gte">greater or equal</SelectItem>
                            <SelectItem value="lt">less than</SelectItem>
                            <SelectItem value="lte">less or equal</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={newStep.skipIfValue}
                          onChange={(e) => setNewStep({ ...newStep, skipIfValue: e.target.value })}
                          placeholder="value"
                          disabled={!newStep.skipIfField}
                          data-testid="input-skip-value"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsStepDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSaveStep}
                disabled={
                  newStep.stepType === 'sms'
                    ? !newStep.smsBody.trim()
                    : !newStep.subjectTemplate || !newStep.bodyTemplate
                }
                data-testid="btn-save-step"
              >
                {editingStep ? "Save Changes" : "Add Step"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Move to Step Dialog */}
        <Dialog open={!!moveStepEnrollment} onOpenChange={(open) => { if (!open) setMoveStepEnrollment(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Move to Step</DialogTitle>
              <DialogDescription>
                Move <strong>{moveStepEnrollment?.contactName || moveStepEnrollment?.contactEmail}</strong> to a different step in this campaign.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Step</Label>
                <Select
                  value={String(moveToStepIndex)}
                  onValueChange={(v) => setMoveToStepIndex(Number(v))}
                >
                  <SelectTrigger data-testid="select-move-step">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {campaignDetails?.steps?.map((step, idx) => (
                      <SelectItem key={step.id} value={String(idx)}>
                        Step {idx + 1}: {step.subjectTemplate.slice(0, 30)}{step.subjectTemplate.length > 30 ? "…" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveStepEnrollment(null)}>Cancel</Button>
              <Button
                onClick={() => {
                  if (moveStepEnrollment) {
                    moveStepMutation.mutate({ enrollmentId: moveStepEnrollment.id, stepIndex: moveToStepIndex });
                  }
                }}
                disabled={moveStepMutation.isPending}
                data-testid="btn-confirm-move-step"
              >
                Move to Step {moveToStepIndex + 1}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={retryDialogOpen}
          onOpenChange={(open) => {
            setRetryDialogOpen(open);
            if (!open) {
              setRetryStepEdits({});
              setRetryGuardError(null);
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="dialog-drip-retry-failed">
            <DialogHeader>
              <DialogTitle>Edit step content & retry failed messages</DialogTitle>
              <DialogDescription>
                Fix the step{Object.keys(retryStepEdits).length === 1 ? "" : "s"} below, then re-queue every failed message
                in this campaign in one go.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4">
              {Object.keys(retryStepEdits).length === 0 ? (
                <p className="text-sm text-muted-foreground">No failed steps detected.</p>
              ) : (
                Object.entries(retryStepEdits).map(([stepId, edits]) => {
                  const step = campaignDetails.steps?.find((s) => s.id === stepId);
                  const stepIdx = campaignDetails.steps?.findIndex((s) => s.id === stepId) ?? -1;
                  const guardForThis = retryGuardError?.stepId === stepId ? retryGuardError : null;
                  return (
                    <div key={stepId} className="rounded-md border p-3 space-y-2" data-testid={`retry-step-${stepId}`}>
                      <p className="text-sm font-medium">
                        Step {stepIdx >= 0 ? stepIdx + 1 : "?"}
                        {step?.subjectTemplate ? ` — ${step.subjectTemplate}` : ""}
                      </p>
                      <div className="space-y-1">
                        <Label htmlFor={`retry-subject-${stepId}`}>Subject</Label>
                        <Input
                          id={`retry-subject-${stepId}`}
                          value={edits.subjectTemplate}
                          onChange={(e) =>
                            setRetryStepEdits((prev) => ({
                              ...prev,
                              [stepId]: { ...prev[stepId], subjectTemplate: e.target.value },
                            }))
                          }
                          data-testid={`input-retry-subject-${stepId}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`retry-body-${stepId}`}>Body</Label>
                        <Textarea
                          id={`retry-body-${stepId}`}
                          rows={6}
                          value={edits.bodyTemplate}
                          onChange={(e) =>
                            setRetryStepEdits((prev) => ({
                              ...prev,
                              [stepId]: { ...prev[stepId], bodyTemplate: e.target.value },
                            }))
                          }
                          data-testid={`textarea-retry-body-${stepId}`}
                        />
                      </div>
                      {guardForThis && (
                        <p className="text-xs text-red-600 dark:text-red-400" data-testid={`text-retry-guard-${stepId}`}>
                          Still leaking: {guardForThis.tokens.map((t) => `{{${t}}}`).join(", ")}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
              {retryGuardError && !retryGuardError.stepId && (
                <p className="text-xs text-red-600 dark:text-red-400" data-testid="text-retry-guard-general">
                  Still leaking: {retryGuardError.tokens.map((t) => `{{${t}}}`).join(", ")}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRetryDialogOpen(false)} data-testid="button-retry-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!selectedCampaign) return;
                  const steps = Object.entries(retryStepEdits).map(([id, e]) => ({
                    id,
                    subject: e.subjectTemplate,
                    body: e.bodyTemplate,
                  }));
                  retryFailedDripMutation.mutate({ campaignId: selectedCampaign.id, steps });
                }}
                disabled={retryFailedDripMutation.isPending || Object.keys(retryStepEdits).length === 0}
                data-testid="button-retry-save"
              >
                {retryFailedDripMutation.isPending ? "Re-queuing..." : "Save & retry failed messages"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isEnrollDialogOpen} onOpenChange={(open) => {
          setIsEnrollDialogOpen(open);
          if (!open) {
            setLeadSearchQuery("");
            setLeadRegionFilter(null);
            setLeadStatusFilter("all");
            setSelectedLeadsForEnroll([]);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Enroll</DialogTitle>
              <DialogDescription>
                {campaignRiskTier === "warm"
                  ? "Warm campaign — select clients to enroll in this drip campaign."
                  : "Select leads or clients to enroll in this drip campaign."}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {campaignRiskTier === "cold" && (
                <div className="flex gap-2" data-testid="toggle-enroll-contact-type">
                  <Button
                    type="button"
                    variant={enrollContactType === "lead" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setEnrollContactType("lead"); setSelectedLeadsForEnroll([]); }}
                    data-testid="btn-enroll-type-leads"
                  >
                    Leads
                  </Button>
                  <Button
                    type="button"
                    variant={enrollContactType === "client" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setEnrollContactType("client"); setSelectedLeadsForEnroll([]); }}
                    data-testid="btn-enroll-type-clients"
                  >
                    Clients
                  </Button>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder={effectiveEnrollType === "client" ? "Search clients by name or email..." : "Search leads by name, email, or account..."}
                  value={leadSearchQuery}
                  onChange={(e) => setLeadSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-lead-search"
                />
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Popover open={regionSearchOpen} onOpenChange={setRegionSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-[180px] justify-start" data-testid="btn-region-filter">
                      {leadRegionFilter ? (regionMap.get(leadRegionFilter) || leadRegionFilter) : "All Regions"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search regions..." />
                      <CommandList>
                        <CommandEmpty>No region found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem onSelect={() => { setLeadRegionFilter(null); setRegionSearchOpen(false); }}>
                            All Regions
                          </CommandItem>
                          {uniqueRegions.map((region) => (
                            <CommandItem 
                              key={region.id} 
                              value={region.name}
                              onSelect={() => { setLeadRegionFilter(region.id); setRegionSearchOpen(false); }}
                            >
                              {region.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                
                {effectiveEnrollType === "lead" && (
                  <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="select-status-filter">
                      <SelectValue placeholder="All Leads" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Leads</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="new">New (Untagged)</SelectItem>
                      <SelectItem value="not_now">Not Now</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                
                {(leadSearchQuery || leadRegionFilter || leadStatusFilter !== "all") && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { 
                      setLeadSearchQuery(""); 
                      setLeadRegionFilter(null); 
                      setLeadStatusFilter("all"); 
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground">
                Showing {filteredContacts.length} {effectiveEnrollType === "client" ? "clients" : "leads"} {selectedLeadsForEnroll.length > 0 && `(${selectedLeadsForEnroll.length} selected)`}
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-1 border rounded-md p-2 min-h-[200px] max-h-[400px]">
              {filteredContacts.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  {enrollableContacts.length === 0 ? (
                    <>
                      <p className="text-sm font-medium text-muted-foreground">
                        {effectiveEnrollType === "client"
                          ? "No clients with email addresses in your workspace yet."
                          : "No leads with email addresses in your workspace yet."}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {effectiveEnrollType === "client"
                          ? "Add clients with a valid email address to enroll them in this campaign."
                          : "Drip campaigns require a valid email address. Add emails to your leads first."}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No matching {effectiveEnrollType === "client" ? "clients" : "leads"} found. Try adjusting your filters.
                    </p>
                  )}
                </div>
              ) : (
                filteredContacts.slice(0, 100).map((contact) => (
                  <label 
                    key={contact.id} 
                    className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                    data-testid={`lead-checkbox-${contact.id}`}
                  >
                    <Checkbox
                      checked={selectedLeadsForEnroll.includes(contact.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedLeadsForEnroll([...selectedLeadsForEnroll, contact.id]);
                        } else {
                          setSelectedLeadsForEnroll(selectedLeadsForEnroll.filter(id => id !== contact.id));
                        }
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{contact.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{contact.email}</p>
                    </div>
                    {contact.regionId && regionMap.get(contact.regionId) && (
                      <Badge variant="outline" className="shrink-0">{regionMap.get(contact.regionId)}</Badge>
                    )}
                    {contact.status && (
                      <Badge variant="secondary" className="shrink-0">{contact.status}</Badge>
                    )}
                  </label>
                ))
              )}
              {filteredContacts.length > 100 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Showing first 100 results. Use filters to narrow down.
                </p>
              )}
            </div>
            
            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => setIsEnrollDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleEnroll} 
                disabled={selectedLeadsForEnroll.length === 0 || enrollContactsMutation.isPending} 
                data-testid="btn-confirm-enroll"
              >
                Enroll {selectedLeadsForEnroll.length} {effectiveEnrollType === "client" ? "Client" : "Lead"}{selectedLeadsForEnroll.length !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <WarmVolumeAckDialog
          open={enrollWarmAckOpen}
          onOpenChange={setEnrollWarmAckOpen}
          count={selectedLeadsForEnroll.length}
          threshold={warmVolumeThreshold}
          prospectOverride={effectiveEnrollType === "lead"}
          onConfirm={() => performEnroll(true)}
          isBusy={enrollContactsMutation.isPending}
        />
      </div>
    );
  }

  const CampaignCard = ({ campaign, showMasterBadge = false }: { campaign: DripCampaign; showMasterBadge?: boolean }) => (
    <Card
      key={campaign.id}
      className="cursor-pointer hover-elevate transition-all"
      onClick={() => handleSelectCampaign(campaign)}
      data-testid={`card-campaign-${campaign.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            <CardTitle className="text-base truncate">{campaign.name}</CardTitle>
            {showMasterBadge && (
              <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400 shrink-0">
                <Crown className="w-3 h-3 mr-1" />
                Master
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {getStatusBadge(campaign.status)}
            {campaign.canEdit !== false && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`btn-campaign-menu-${campaign.id}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {campaign.status === "active" && (
                    <DropdownMenuItem
                      onClick={() => pauseCampaignMutation.mutate(campaign.id)}
                      data-testid={`menu-pause-${campaign.id}`}
                    >
                      <Pause className="w-4 h-4 mr-2 text-yellow-600" />
                      Pause
                    </DropdownMenuItem>
                  )}
                  {campaign.status === "draft" && (
                    <DropdownMenuItem
                      onClick={() => activateCampaignMutation.mutate(campaign.id)}
                      data-testid={`menu-launch-${campaign.id}`}
                    >
                      <Play className="w-4 h-4 mr-2 text-green-600" />
                      Launch
                    </DropdownMenuItem>
                  )}
                  {campaign.status === "paused" && (
                    <DropdownMenuItem
                      onClick={() => activateCampaignMutation.mutate(campaign.id)}
                      data-testid={`menu-unpause-${campaign.id}`}
                    >
                      <Play className="w-4 h-4 mr-2 text-green-600" />
                      Unpause
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleSelectCampaign(campaign)}
                    data-testid={`menu-edit-${campaign.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => duplicateCampaignMutation.mutate(campaign.id)}
                    data-testid={`menu-duplicate-${campaign.id}`}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => deleteCampaignMutation.mutate(campaign.id)}
                    data-testid={`menu-delete-${campaign.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <Mail className="w-4 h-4" />
            <span>{campaign.stepCount || 0} steps</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{campaign.activeEnrollments || 0} active</span>
          </div>
          {campaign.maxSendsPerDay != null && (
            <div className="flex items-center gap-1" data-testid={`badge-daily-cap-${campaign.id}`}>
              <span className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5">
                Today: {campaign.sentToday ?? 0}/{campaign.maxSendsPerDay}
              </span>
            </div>
          )}
        </div>
        {campaign.scope === "MASTER" && campaign.assignedToUserId && (
          <p className="text-xs text-muted-foreground mt-2">
            Assigned: {campaign.assignedToUserId === "ALL_SALES_REPS"
              ? "All Sales Reps"
              : users.find(u => u.id === campaign.assignedToUserId)?.name || "User"}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Drip Campaigns</h2>
          <p className="text-sm text-muted-foreground">Automated email sequences that nurture leads over time</p>
        </div>
        {permissions?.canCreateCampaigns && (
          <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="btn-create-campaign">
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        )}
      </div>

      {gmailStatus && !gmailStatus.inboundReadingAvailable && (
        <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <WifiOff className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Reply detection is offline.</span> "Pause on reply" won't trigger until at least one user connects their personal Gmail account.{" "}
            Go to <strong>Settings → Gmail</strong> and connect your Gmail to enable inbox reading.
          </AlertDescription>
        </Alert>
      )}

      {/* M4: workspace-wide leaderboard. Hidden when there's nothing to rank. */}
      <DripLeaderboardCard />

      {campaignsLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="p-12 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Drip Campaigns</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Create your first drip campaign to automate email sequences for nurturing leads.
          </p>
          {permissions?.canCreateCampaigns && (
            <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="btn-create-first-campaign">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Campaign
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-8">
          {myCampaigns.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold">My Drip Campaigns</h3>
                <Badge variant="secondary">{myCampaigns.length}</Badge>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {myCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} />
                ))}
              </div>
            </div>
          )}

          {masterCampaigns.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold">Master Drip Campaigns</h3>
                <Badge variant="secondary">{masterCampaigns.length}</Badge>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {masterCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} showMasterBadge />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl !flex flex-col max-h-[90vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle>Create Drip Campaign</DialogTitle>
            <DialogDescription>Create a new automated email sequence.</DialogDescription>
          </DialogHeader>

          {/* Task #317 — scrollable middle so the dialog never runs off
              the top/bottom once advanced options are expanded. Header +
              footer stay pinned. */}
          <div className="flex-1 overflow-y-auto px-1 -mx-1">
          {permissions?.isAdmin && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="admin-scope-ribbon">
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Campaign Type:</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={newCampaign.scope === "MASTER" ? "default" : "outline"}
                  onClick={() => setNewCampaign({ ...newCampaign, scope: "MASTER" })}
                  className={newCampaign.scope === "MASTER" ? "bg-amber-600 hover:bg-amber-700" : ""}
                  data-testid="btn-scope-master"
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Master Campaign
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={newCampaign.scope === "USER" ? "default" : "outline"}
                  onClick={() => setNewCampaign({ ...newCampaign, scope: "USER" })}
                  className={newCampaign.scope === "USER" ? "bg-amber-600 hover:bg-amber-700" : ""}
                  data-testid="btn-scope-personal"
                >
                  <User className="w-3 h-3 mr-1" />
                  My Campaign
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Task #317 — Warm/Cold outreach selector. Default warm.
                Cold is gated behind a verified cold sending domain so a
                cold campaign can't be created before cold sending is set
                up. The choice is carried into the create payload. */}
            <div className="space-y-2">
              <Label>Outreach type</Label>
              <div className="inline-flex rounded-lg border p-1 bg-muted/40" data-testid="toggle-risk-tier">
                <Button
                  type="button"
                  size="sm"
                  variant={newCampaign.riskTier === "warm" ? "default" : "ghost"}
                  onClick={() => setNewCampaign({ ...newCampaign, riskTier: "warm" })}
                  data-testid="btn-risk-warm"
                >
                  <Flame className="w-3.5 h-3.5 mr-1.5" />
                  Warm
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={newCampaign.riskTier === "cold" ? "default" : "ghost"}
                  disabled={!coldDomainVerified}
                  onClick={() => setNewCampaign({ ...newCampaign, riskTier: "cold" })}
                  data-testid="btn-risk-cold"
                >
                  <Snowflake className="w-3.5 h-3.5 mr-1.5" />
                  Cold
                </Button>
              </div>
              {newCampaign.riskTier === "warm" && (
                <p className="text-xs text-muted-foreground" data-testid="text-warm-helper">
                  Use Warm only for clients and leads you've physically communicated with in the past.
                </p>
              )}
              {newCampaign.riskTier === "warm" && massEmailSettings?.lockColdOverrideForNonAdmins && !permissions?.isAdmin && (
                <div
                  className="flex items-start gap-2 p-2.5 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"
                  data-testid="notice-warm-override-locked"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p className="text-xs">
                    Your workspace admin requires cold outreach for lead/prospect lists. Any leads you
                    enroll will be sent through the cold sending domain instead of your inbox. Client
                    enrollments are unaffected.
                  </p>
                </div>
              )}
              {newCampaign.riskTier === "cold" && coldDomainVerified && (
                <p className="text-xs text-muted-foreground" data-testid="text-cold-helper">
                  Cold outreach routes through your verified cold sending domain, so it never touches your regular inbox.
                </p>
              )}
              {newCampaign.riskTier === "cold" && coldDomainVerified && (
                <div className="flex items-start justify-between gap-3 rounded-md border p-2.5" data-testid="row-forward-cold-replies-create">
                  <div className="space-y-0.5">
                    <Label htmlFor="forward-cold-replies-create" className="text-xs font-medium">
                      Forward replies to a person
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When a prospect replies on a managed (Lease/Buy) cold mailbox, forward a copy to your workspace forward target. Set the destination in Email → Cold Email.
                    </p>
                  </div>
                  <Switch
                    id="forward-cold-replies-create"
                    checked={newCampaign.forwardColdReplies}
                    onCheckedChange={(v) => setNewCampaign({ ...newCampaign, forwardColdReplies: v })}
                    data-testid="switch-forward-cold-replies-create"
                  />
                </div>
              )}
              {coldSendingDomainQuery.isLoading ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="text-cold-checking">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Checking cold sending setup…
                </p>
              ) : !coldDomainVerified ? (
                <div
                  className="flex items-start gap-2 p-2.5 rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200"
                  data-testid="notice-cold-not-ready"
                >
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <p className="text-xs">
                    Cold outreach isn't available yet — it needs a verified cold sending domain
                    {permissions?.isAdmin ? (
                      <>
                        {" "}
                        <Link
                          href="/admin?section=emailSettings&tab=cold&byod=1"
                          className="underline font-medium"
                          data-testid="link-cold-setup"
                        >
                          set one up in Email settings
                        </Link>
                        .
                      </>
                    ) : (
                      <>, which an admin can set up in Email settings.</>
                    )}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Campaign Name</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="New Lead Nurture Sequence"
                data-testid="input-campaign-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Send From</Label>
              {permissions?.isAdmin ? (
                <Select 
                  value={newCampaign.fromEmail || permissions.userEmail || ""} 
                  onValueChange={(email) => {
                    const user = users.find(u => u.email === email);
                    setNewCampaign({ 
                      ...newCampaign, 
                      fromEmail: email,
                      fromName: user?.name || ""
                    });
                  }}
                >
                  <SelectTrigger data-testid="select-from-email">
                    <SelectValue placeholder="Select sender" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.email}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{permissions?.userEmail || "Your email address"}</span>
                </div>
              )}
            </div>

            {/* M1: Trigger picker — always visible (simple mode) */}
            <div className="space-y-2">
              <Label>When should this campaign start?</Label>
              <Select
                value={newCampaign.triggerType}
                onValueChange={(val) => {
                  const meta = TRIGGER_TYPES.find(t => t.value === val);
                  setNewCampaign({
                    ...newCampaign,
                    triggerType: val,
                    triggerConfigValue: meta?.configDefault ?? 0,
                  });
                }}
              >
                <SelectTrigger data-testid="select-trigger-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} data-testid={`option-trigger-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const meta = TRIGGER_TYPES.find(t => t.value === newCampaign.triggerType);
                if (!meta?.configKey) {
                  return newCampaign.triggerType === "manual" ? (
                    <p className="text-xs text-muted-foreground">
                      You'll enroll leads or clients into this campaign by hand.
                    </p>
                  ) : null;
                }
                return (
                  <div className="flex items-center gap-2 pl-1 pt-1">
                    <Label className="text-sm whitespace-nowrap">{meta.configLabel}:</Label>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      value={newCampaign.triggerConfigValue}
                      onChange={(e) => setNewCampaign({ ...newCampaign, triggerConfigValue: parseInt(e.target.value) || 0 })}
                      data-testid="input-trigger-config"
                    />
                    {meta.configHint && (
                      <span className="text-xs text-muted-foreground">{meta.configHint}</span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* M1: Show advanced options toggle */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Switch
                checked={showAdvancedCreate}
                onCheckedChange={setShowAdvancedCreate}
                data-testid="switch-show-advanced"
              />
              <Label>Show advanced options</Label>
            </div>

            {showAdvancedCreate && (
              <div className="space-y-4 p-3 rounded-md border bg-muted/30">
                <div className="text-sm font-medium">Auto-exit rules</div>
                <div className="space-y-2 pl-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.autoExitOnReplied}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, autoExitOnReplied: v })}
                      data-testid="switch-exit-on-replied"
                    />
                    <Label className="text-sm">Stop sending if the contact replies</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.autoExitOnBooked}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, autoExitOnBooked: v })}
                      data-testid="switch-exit-on-booked"
                    />
                    <Label className="text-sm">Stop sending if the lead becomes a booking</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.autoExitOnDisqualified}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, autoExitOnDisqualified: v })}
                      data-testid="switch-exit-on-disqualified"
                    />
                    <Label className="text-sm">Stop sending if the lead is marked dead/lost</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={gmailStatus?.inboundReadingAvailable ? newCampaign.autoPauseOnReply : false}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, autoPauseOnReply: v })}
                      disabled={!gmailStatus?.inboundReadingAvailable}
                      data-testid="switch-pause-on-reply"
                    />
                    <Label className="text-sm">
                      Pause (don't auto-exit) on reply if exit-on-reply is off
                      {!gmailStatus?.inboundReadingAvailable && (
                        <span className="block text-xs text-muted-foreground">
                          Needs inbox read access (reply detection) — coming soon. Request it in Settings → Connections.
                        </span>
                      )}
                    </Label>
                  </div>
                </div>

                <div className="text-sm font-medium pt-2">Send timing</div>
                <div className="space-y-3 pl-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.respectBusinessHours}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, respectBusinessHours: v })}
                      data-testid="switch-business-hours"
                    />
                    <Label className="text-sm">Only send during business hours ({getTimeZoneAbbreviation()})</Label>
                  </div>
                  {newCampaign.respectBusinessHours && (
                    <div className="flex items-center gap-2 pl-6">
                      <Input
                        type="time"
                        className="w-28"
                        value={newCampaign.businessHoursStartLocal}
                        onChange={(e) => setNewCampaign({ ...newCampaign, businessHoursStartLocal: e.target.value })}
                        data-testid="input-bh-start"
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input
                        type="time"
                        className="w-28"
                        value={newCampaign.businessHoursEndLocal}
                        onChange={(e) => setNewCampaign({ ...newCampaign, businessHoursEndLocal: e.target.value })}
                        data-testid="input-bh-end"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Spread sends by ±</Label>
                    <Input
                      type="number"
                      min={0}
                      max={360}
                      className="w-20"
                      value={newCampaign.sendTimeSpreadMinutes}
                      onChange={(e) => setNewCampaign({ ...newCampaign, sendTimeSpreadMinutes: parseInt(e.target.value) || 0 })}
                      data-testid="input-send-spread"
                    />
                    <span className="text-sm text-muted-foreground">minutes (jitter so sends don't look bot-like)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Daily send cap</Label>
                    <Input
                      type="number"
                      min={1}
                      className="w-24"
                      placeholder="No cap"
                      value={newCampaign.maxSendsPerDay ?? ""}
                      onChange={(e) => setNewCampaign({ ...newCampaign, maxSendsPerDay: e.target.value ? parseInt(e.target.value) || 1 : null })}
                      data-testid="input-max-sends-per-day"
                    />
                    <span className="text-sm text-muted-foreground">emails/day max (blank = no limit)</span>
                  </div>
                </div>

                <div className="text-sm font-medium pt-2">Sender behavior</div>
                <div className="space-y-2 pl-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.sendAsAssignedRep}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, sendAsAssignedRep: v, sendAsRoundRobin: v ? false : newCampaign.sendAsRoundRobin })}
                      data-testid="switch-send-as-assigned"
                    />
                    <Label className="text-sm">Send from the contact's assigned rep</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.sendAsRoundRobin}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, sendAsRoundRobin: v, sendAsAssignedRep: v ? false : newCampaign.sendAsAssignedRep })}
                      data-testid="switch-send-as-rr"
                    />
                    <Label className="text-sm">Round-robin sends across active sales reps</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCampaign.replyTrickEnabled}
                      onCheckedChange={(v) => setNewCampaign({ ...newCampaign, replyTrickEnabled: v })}
                      data-testid="switch-reply-trick"
                    />
                    <Label className="text-sm">Send follow-ups as a reply to the previous email</Label>
                  </div>
                </div>

                {/* M6: Audience segment — opt-in dynamic enrollment.
                    A polled sweep enrolls every matching contact every
                    5 minutes. Idempotent via existing dedupe. */}
                <div className="text-sm font-medium pt-2">Target audience (auto-enroll)</div>
                <div className="space-y-3 pl-1">
                  <p className="text-xs text-muted-foreground">
                    Optional. When set, every matching contact is auto-enrolled within ~5 min.
                    Already-enrolled contacts are skipped, so it's safe to leave on.
                  </p>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Match</Label>
                    <Select
                      value={newCampaign.targetSegmentEntity}
                      onValueChange={(v: "lead" | "client") => setNewCampaign({ ...newCampaign, targetSegmentEntity: v, targetSegmentRules: [] })}
                    >
                      <SelectTrigger className="w-32" data-testid="select-segment-entity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lead">Leads</SelectItem>
                        <SelectItem value="client">Clients</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">that match all of:</span>
                  </div>
                  {newCampaign.targetSegmentRules.map((rule, idx) => {
                    const fieldOptions = newCampaign.targetSegmentEntity === "client"
                      ? [
                          { value: "client.totalSpend", label: "Total spend", ops: ["gt", "gte", "lt", "lte", "eq"] },
                          { value: "client.region", label: "Region", ops: ["eq", "neq"] },
                          { value: "client.tags", label: "Tag", ops: ["contains", "notContains"] },
                        ]
                      : [
                          { value: "lead.status", label: "Status", ops: ["eq", "neq"] },
                          { value: "lead.source", label: "Source", ops: ["eq", "neq"] },
                          { value: "lead.priority", label: "Priority", ops: ["eq", "neq"] },
                          { value: "lead.tags", label: "Tag", ops: ["contains", "notContains"] },
                          { value: "lead.assignedTo", label: "Assigned to", ops: ["eq", "neq"] },
                        ];
                    const currentField = fieldOptions.find(f => f.value === rule.field) || fieldOptions[0];
                    const opLabels: Record<string, string> = {
                      eq: "equals", neq: "not equals",
                      gt: "greater than", gte: "≥", lt: "less than", lte: "≤",
                      contains: "includes", notContains: "doesn't include",
                    };
                    return (
                      <div key={idx} className="flex items-center gap-2" data-testid={`segment-rule-${idx}`}>
                        <Select
                          value={rule.field}
                          onValueChange={(v) => {
                            const newRules = [...newCampaign.targetSegmentRules];
                            const newField = fieldOptions.find(f => f.value === v) || fieldOptions[0];
                            newRules[idx] = { field: v, op: newField.ops[0], value: "" };
                            setNewCampaign({ ...newCampaign, targetSegmentRules: newRules });
                          }}
                        >
                          <SelectTrigger className="w-36" data-testid={`select-segment-field-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldOptions.map((f) => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={rule.op}
                          onValueChange={(v) => {
                            const newRules = [...newCampaign.targetSegmentRules];
                            newRules[idx] = { ...rule, op: v };
                            setNewCampaign({ ...newCampaign, targetSegmentRules: newRules });
                          }}
                        >
                          <SelectTrigger className="w-32" data-testid={`select-segment-op-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currentField.ops.map((o) => (
                              <SelectItem key={o} value={o}>{opLabels[o] || o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={rule.value}
                          onChange={(e) => {
                            const newRules = [...newCampaign.targetSegmentRules];
                            newRules[idx] = { ...rule, value: e.target.value };
                            setNewCampaign({ ...newCampaign, targetSegmentRules: newRules });
                          }}
                          className="flex-1"
                          placeholder="value"
                          data-testid={`input-segment-value-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newRules = newCampaign.targetSegmentRules.filter((_, i) => i !== idx);
                            setNewCampaign({ ...newCampaign, targetSegmentRules: newRules });
                          }}
                          data-testid={`btn-segment-remove-${idx}`}
                        >
                          ×
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const defaultField = newCampaign.targetSegmentEntity === "client" ? "client.totalSpend" : "lead.status";
                      const defaultOp = newCampaign.targetSegmentEntity === "client" ? "gt" : "eq";
                      setNewCampaign({
                        ...newCampaign,
                        targetSegmentRules: [...newCampaign.targetSegmentRules, { field: defaultField, op: defaultOp, value: "" }],
                      });
                    }}
                    data-testid="btn-segment-add-rule"
                  >
                    + Add rule
                  </Button>
                </div>
              </div>
            )}

            {permissions?.isAdmin && newCampaign.scope === "MASTER" && (
              <>
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select 
                    value={newCampaign.assignedToUserId || ""} 
                    onValueChange={(val) => setNewCampaign({ ...newCampaign, assignedToUserId: val || null })}
                  >
                    <SelectTrigger data-testid="select-assign-to">
                      <SelectValue placeholder="Select user or group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL_SALES_REPS">All Sales Reps</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Master campaigns can be assigned to specific users or all sales reps
                  </p>
                </div>

                <div className="space-y-3 p-3 rounded-md border">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={newCampaign.autoEnrollNewLeads}
                      onCheckedChange={(checked) => setNewCampaign({ ...newCampaign, autoEnrollNewLeads: checked })}
                      data-testid="switch-auto-enroll-create"
                    />
                    <Label>Auto-enroll new leads</Label>
                  </div>
                  {newCampaign.autoEnrollNewLeads && (
                    <div className="flex items-center gap-2 pl-6">
                      <Label className="text-sm">Delay:</Label>
                      <Input 
                        type="number" 
                        min="0"
                        className="w-20"
                        value={newCampaign.autoEnrollDelayMinutes}
                        onChange={(e) => setNewCampaign({ ...newCampaign, autoEnrollDelayMinutes: parseInt(e.target.value) || 0 })}
                        data-testid="input-auto-enroll-delay-create"
                      />
                      <Select
                        value={newCampaign.autoEnrollDelayUnit}
                        onValueChange={(value: "minutes" | "days") => setNewCampaign({ ...newCampaign, autoEnrollDelayUnit: value })}
                      >
                        <SelectTrigger className="w-28" data-testid="select-auto-enroll-delay-unit-create">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">minutes</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    When enabled, new leads will be automatically enrolled after the specified delay
                  </p>
                </div>
              </>
            )}
          </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCampaign}
              disabled={!newCampaign.name || createCampaignMutation.isPending}
              data-testid="btn-confirm-create-campaign"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
